import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SaveIcon from '@mui/icons-material/Save';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import reelProjectServiceInstance from '../../services/ReelProjectService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { reelEditorHelpItems } from '../../utils/helpItems';
import ColorField from '../common/ColorField';
import HelpOverlay from '../common/HelpOverlay';
import NumericField from '../common/NumericField';
import { PageHeader } from '../common/PageHeader';
import FolderBreadcrumbs from '../navigation/ModernBreadcrumbs';
import BulkReelExportDialog from './BulkReelExportDialog';
import SubtitleTimeline from './SubtitleTimeline';

const MIN_ACTION_DURATION = 0.4;

const WORD_SPECIFIC_STYLE_KEYS = [
    'emphasisWords',
    'emphasisScale',
    'emphasisBold',
    'emphasisUnderline',
    'emphasisStylePreset',
    'emphasisColor',
    'emphasisBgColor',
    'emphasisBgOpacity',
    'emphasisStrokeColor',
    'emphasisStrokeWidth',
    'emphasisShadowColor',
    'emphasisShadowOpacity',
    'emphasisShadowBlur',
    'emphasisShadowOffsetY',
    'emphasisItalic',
    'emphasisUppercase',
    'emphasisLetterSpacing',
    'emphasisFontWeight',
];

const defaultShapeOverlay = {
    enabled: false,
    type: 'rounded-box',
    xPercent: 10,
    yPercent: 10,
    widthPercent: 28,
    heightPercent: 14,
    rotationDeg: 0,
    fillColor: '#2563eb',
    fillOpacity: 26,
    borderColor: '#ffffff',
    borderOpacity: 65,
    borderWidth: 2,
    radiusPx: 14,
    fadeInMs: 0,
    fadeOutMs: 0,
};

const defaultLogoOverlay = {
    enabled: false,
    imageUrl: '',
    xPercent: 80,
    yPercent: 6,
    widthPercent: 14,
    heightPercent: 14,
    opacity: 88,
    fit: 'contain',
    startSec: null,   // null = always show
    endSec: null,     // null = always show
    fadeInMs: 0,
    fadeOutMs: 0,
};

const platformPresets = {
    horizontal: { name: 'Horizontal', width: 1920, height: 1080, ratio: '16:9' },
    vertical: { name: 'Vertical', width: 1080, height: 1920, ratio: '9:16' },
    square: { name: 'Square', width: 1080, height: 1080, ratio: '1:1' },
};

const FONT_OPTIONS = [
    'Poppins',
    'Inter',
    'Montserrat',
    'Roboto',
    'Lato',
    'Open Sans',
    'Nunito',
    'Merriweather',
    'Playfair Display',
    'Bebas Neue',
    'Raleway',
    'Oswald',
    'Anton',
    'Bangers',
    'Pacifico',
    'Caveat',
    'Abril Fatface',
];

const defaultStyle = {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#ffffff',
    backgroundColor: '#000000',
    backgroundOpacity: 45,
    bold: true,
    italic: false,
    underline: false,
    align: 'center',
    position: 'bottom-center',
    lineHeight: 1.2,
    letterSpacing: 0,
    offsetX: 0,
    offsetY: 0,
    boxWidthPercent: 82,
    boxPaddingX: 14,
    boxPaddingY: 8,
    boxRadius: 10,
    emphasisWords: '',
    emphasisScale: 1.35,
    emphasisBold: true,
    emphasisUnderline: false,
    emphasisStylePreset: 'scale',
    emphasisColor: '#ffffff',
    emphasisBgColor: '#000000',
    emphasisBgOpacity: 0,
    emphasisStrokeColor: '#000000',
    emphasisStrokeWidth: 0,
    emphasisShadowColor: '#ffffff',
    emphasisShadowOpacity: 55,
    emphasisShadowBlur: 8,
    emphasisShadowOffsetY: 2,
    emphasisItalic: false,
    emphasisUppercase: false,
    emphasisLetterSpacing: 0,
    emphasisFontWeight: 800,
    localShapeEnabled: false,
    localShapeType: 'rounded-box',
    localShapeXPercent: 8,
    localShapeYPercent: 8,
    localShapeWidthPercent: 22,
    localShapeHeightPercent: 12,
    localShapeRotationDeg: 0,
    localShapeFillColor: '#f59e0b',
    localShapeFillOpacity: 20,
    localShapeBorderColor: '#ffffff',
    localShapeBorderOpacity: 70,
    localShapeBorderWidth: 2,
    localShapeRadiusPx: 12,
    localLogoEnabled: false,
    localLogoImageUrl: '',
    localLogoXPercent: 78,
    localLogoYPercent: 8,
    localLogoWidthPercent: 12,
    localLogoHeightPercent: 12,
    localLogoOpacity: 90,
    localLogoFit: 'contain',
    fadeInMs: 120,
    fadeOutMs: 120,
    textAnimationIn: 'none',
    textAnimationOut: 'none',
    animationDurationMs: 250,
    karaokeHighlightColor: '#FFD400',
    typewriterCps: 18,
    wordTimings: [],
};

const TEXT_ANIMATION_IN_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'fade', label: 'Fade' },
    { value: 'pop', label: 'Pop' },
    { value: 'slideUp', label: 'Slide Up' },
    { value: 'karaoke', label: 'Karaoke' },
    { value: 'typewriter', label: 'Typewriter' },
];

const SUBTITLE_ANCHOR_MARGIN_X = 20;
const SUBTITLE_ANCHOR_MARGIN_Y = 30;

const buildActionLabel = (action) => {
    const text = action?.text || '';
    const preview = text.replace(/\s+/g, ' ').trim();
    if (!preview) return 'Empty subtitle';
    if (preview.length < 48) return preview;
    return `${preview.slice(0, 45)}...`;
};

const toSeconds = (timecode) => {
    if (!timecode) return 0;
    const s = timecode.trim();
    // VTT timestamps can be HH:MM:SS.mmm, MM:SS.mmm, or even with comma and varying decimal digits
    const match = s.match(/^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d+)$/);
    if (!match) return 0;
    const hours = match[1] !== undefined ? Number(match[1]) : 0;
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);
    // Normalise fractional part to milliseconds (pad/truncate to 3 digits)
    const millisRaw = match[4].slice(0, 3).padEnd(3, '0');
    const millis = Number(millisRaw);
    return hours * 3600 + minutes * 60 + seconds + millis / 1000;
};

const parseVtt = (vttText) => {
    const lines = (vttText || '').split(/\r?\n/);
    const cues = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line || line === 'WEBVTT') {
            i += 1;
            continue;
        }

        const timingLine = line.includes('-->') ? line : (lines[i + 1] || '').trim();
        if (!timingLine.includes('-->')) {
            i += 1;
            continue;
        }

        const [startRaw, endRaw] = timingLine.split('-->').map((part) => part.trim().split(' ')[0]);
        const start = toSeconds(startRaw);
        const end = toSeconds(endRaw);

        i += line.includes('-->') ? 1 : 2;
        const textLines = [];
        while (i < lines.length && lines[i].trim() !== '') {
            textLines.push(lines[i]);
            i += 1;
        }

        if (end > start) {
            cues.push({ start, end, text: textLines.join('\n').trim() });
        }

        i += 1;
    }

    return cues;
};

const makeSubtitleAction = (cue, index) => ({
    id: `subtitle-${index + 1}-${Math.floor(cue.start * 1000)}`,
    start: Number(cue.start.toFixed(3)),
    end: Number(Math.max(cue.end, cue.start + MIN_ACTION_DURATION).toFixed(3)),
    effectId: 'subtitle',
    movable: true,
    flexible: true,
    text: cue.text || '',
    style: { ...defaultStyle },
});

const formatSrtTimestamp = (secondsValue) => {
    const safeSeconds = Math.max(0, Number(secondsValue) || 0);
    const totalMs = Math.round(safeSeconds * 1000);
    const hours = Math.floor(totalMs / 3600000);
    const minutes = Math.floor((totalMs % 3600000) / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const millis = totalMs % 1000;
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    const mmm = String(millis).padStart(3, '0');
    return `${hh}:${mm}:${ss},${mmm}`;
};

const buildSrtFromActions = (actions) => {
    const list = Array.isArray(actions) ? [...actions] : [];
    const normalized = list
        .filter((action) => Number.isFinite(Number(action?.start)) && Number.isFinite(Number(action?.end)))
        .map((action) => {
            const start = Number(action.start);
            const end = Math.max(Number(action.end), start + MIN_ACTION_DURATION);
            const text = String(action?.text || '').replace(/\r/g, '').trim();
            return { start, end, text };
        })
        .filter((action) => action.end > action.start && action.text.length > 0)
        .sort((a, b) => a.start - b.start);

    if (normalized.length === 0) return '';

    return normalized
        .map((action, index) => {
            const cueIndex = index + 1;
            const start = formatSrtTimestamp(action.start);
            const end = formatSrtTimestamp(action.end);
            return `${cueIndex}\n${start} --> ${end}\n${action.text}`;
        })
        .join('\n\n');
};

const extractVideoUrlFromUnknown = (payload) => {
    if (!payload || typeof payload !== 'object') return '';

    if(payload?.compressedUrl != null) {
        return String(payload.compressedUrl).trim();
    }

    return '';
};

const extractVideoUrlFromTimelineJson = (timelineJsonText) => {
    try {
        const parsed = JSON.parse(timelineJsonText || '{}');
        const tracks = Array.isArray(parsed?.tracks) ? parsed.tracks : [];
        for (const track of tracks) {
            if (track?.type !== 'video') continue;
            const clips = Array.isArray(track?.clips) ? track.clips : [];
            for (const clip of clips) {
                if (typeof clip?.src === 'string' && clip.src.trim().length > 0) {
                    return clip.src.trim();
                }
            }
        }
    } catch (error) {
        // Ignore invalid timeline JSON here; save/load validation handles schema issues.
    }
    return '';
};

const getOverlayPositionStyle = (position) => {
    switch (position) {
        case 'top-left':
            return { top: SUBTITLE_ANCHOR_MARGIN_Y, left: SUBTITLE_ANCHOR_MARGIN_X, right: 'auto', bottom: 'auto', baseTransform: '' };
        case 'top-right':
            return { top: SUBTITLE_ANCHOR_MARGIN_Y, left: 'auto', right: SUBTITLE_ANCHOR_MARGIN_X, bottom: 'auto', baseTransform: '' };
        case 'top-center':
            return { top: SUBTITLE_ANCHOR_MARGIN_Y, left: '50%', right: 'auto', bottom: 'auto', baseTransform: 'translateX(-50%)' };
        case 'middle-left':
            return { top: '50%', left: 20, right: 'auto', bottom: 'auto', baseTransform: 'translateY(-50%)' };
        case 'middle-center':
            return { top: '50%', left: '50%', right: 'auto', bottom: 'auto', baseTransform: 'translate(-50%, -50%)' };
        case 'middle-right':
            return { top: '50%', left: 'auto', right: 20, bottom: 'auto', baseTransform: 'translateY(-50%)' };
        case 'bottom-left':
            return {
                top: 'auto',
                left: SUBTITLE_ANCHOR_MARGIN_X,
                right: 'auto',
                bottom: SUBTITLE_ANCHOR_MARGIN_Y,
                baseTransform: '',
            };
        case 'bottom-right':
            return {
                top: 'auto',
                left: 'auto',
                right: SUBTITLE_ANCHOR_MARGIN_X,
                bottom: SUBTITLE_ANCHOR_MARGIN_Y,
                baseTransform: '',
            };
        case 'bottom-center':
        default:
            return {
                top: 'auto',
                left: '50%',
                right: 'auto',
                bottom: SUBTITLE_ANCHOR_MARGIN_Y,
                baseTransform: 'translateX(-50%)',
            };
    }
};

const normalizeToken = (token) =>
    token
        .toLowerCase()
        .replace(/[.,!?;:()[\]{}"']/g, '')
        .trim();

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hex) => {
    const clean = String(hex || '').replace('#', '').trim();
    if (![3, 6].includes(clean.length)) return null;
    const normalized =
        clean.length === 3
            ? clean
                  .split('')
                  .map((c) => `${c}${c}`)
                  .join('')
            : clean;
    const int = Number.parseInt(normalized, 16);
    if (!Number.isFinite(int)) return null;
    return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255,
    };
};

const resolveSubtitleBackground = (style) => {
    const color = style?.backgroundColor || defaultStyle.backgroundColor;
    const opacityPercent = Number(style?.backgroundOpacity);
    const alpha = Number.isFinite(opacityPercent)
        ? clamp(opacityPercent, 0, 100) / 100
        : defaultStyle.backgroundOpacity / 100;

    if (typeof color === 'string' && color.startsWith('rgba(')) {
        return color;
    }

    if (typeof color === 'string' && color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
    }

    const rgb = hexToRgb(color);
    if (!rgb) {
        return `rgba(0, 0, 0, ${alpha})`;
    }

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const resolveColorWithOpacity = (hexColor, opacityPercent, fallback = '#000000') => {
    const rgb = hexToRgb(hexColor || fallback) || hexToRgb(fallback) || { r: 0, g: 0, b: 0 };
    const alpha = clamp(Number(opacityPercent ?? 100), 0, 100) / 100;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const resolveEmphasisBackground = (style) => {
    const color = style?.emphasisBgColor || '#000000';
    const alpha = clamp(Number(style?.emphasisBgOpacity ?? 0), 0, 100) / 100;
    const rgb = hexToRgb(color);
    if (!rgb) return `rgba(0,0,0,${alpha})`;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const resolveEmphasisShadow = (style) => {
    const shadowColor = style?.emphasisShadowColor || '#ffffff';
    const rgb = hexToRgb(shadowColor) || { r: 255, g: 255, b: 255 };
    const alpha = clamp(Number(style?.emphasisShadowOpacity ?? 55), 0, 100) / 100;
    const blur = Math.max(0, Number(style?.emphasisShadowBlur ?? 8));
    const offsetY = Number(style?.emphasisShadowOffsetY ?? 2);
    return `0 ${offsetY}px ${blur}px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

/**
 * Parses a CSS text-shadow string (e.g. "0 1px 3px rgba(0,0,0,0.8)") 
 * into components compatible with the backend.
 */
const parseCssTextShadow = (cssShadowString) => {
    const defaultShadow = { offsetX: 0, offsetY: 0, blur: 0, color: '#000000', opacity: 0 };
    if (!cssShadowString || cssShadowString === 'none') return defaultShadow;

    // Matches: [offsetX] [offsetY] [blur] [color]
    const match = cssShadowString.match(/(-?\d+px)?\s*(-?\d+px)?\s*(\d+px)?\s*(rgba?\(.*?\)|#[a-fA-F0-9]+)/);

    if (match) {
        const offsetX = parseInt(match[1]) || 0;
        const offsetY = parseInt(match[2]) || 0;
        const blur = parseInt(match[3]) || 0;
        const colorPart = match[4];

        let color = '#000000';
        let opacity = 100;

        if (colorPart.startsWith('rgba')) {
            const rgbaMatch = colorPart.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
            if (rgbaMatch) {
                color = `#${((1 << 24) + (parseInt(rgbaMatch[1]) << 16) + (parseInt(rgbaMatch[2]) << 8) + parseInt(rgbaMatch[3])).toString(16).slice(1)}`;
                opacity = Math.round(parseFloat(rgbaMatch[4] || 1) * 100);
            }
        } else if (colorPart.startsWith('#')) {
            color = colorPart;
        }

        return { offsetX, offsetY, blur, color, opacity };
    }
    return defaultShadow;
};

const buildEmphasisTokenStyle = (style) => {
    const emphasisScale = Math.max(1, Number(style?.emphasisScale || 1.35));
    const emphasisBold = style?.emphasisBold !== undefined ? Boolean(style.emphasisBold) : true;
    const emphasisUnderline = Boolean(style?.emphasisUnderline);
    const preset = String(style?.emphasisStylePreset || 'scale');
    const baseTokenStyle = {
        fontSize: `${emphasisScale}em`,
        fontWeight: emphasisBold ? Number(style?.emphasisFontWeight || 800) : 400,
        color: style?.emphasisColor || style?.color || '#ffffff',
        fontStyle: style?.emphasisItalic ? 'italic' : 'normal',
        textDecoration: emphasisUnderline ? 'underline' : 'none',
        letterSpacing: `${Number(style?.emphasisLetterSpacing || 0)}px`,
        textTransform: style?.emphasisUppercase ? 'uppercase' : 'none',
        display: 'inline-block',
        lineHeight: 1,
    };

    const presetStyleMap = {
        scale: {},
        highlight: {
            background: resolveEmphasisBackground({ ...style, emphasisBgOpacity: style?.emphasisBgOpacity ?? 55 }),
            borderRadius: '6px',
            padding: '0 0.1em',
        },
        outline: {
            WebkitTextStroke: `${Math.max(0.008, Number(style?.emphasisStrokeWidth || 2) * 0.008)}em ${
                style?.emphasisStrokeColor || '#000000'
            }`,
        },
        glow: {
            textShadow: resolveEmphasisShadow({ ...style, emphasisShadowBlur: style?.emphasisShadowBlur ?? 14 }),
        },
        accent: {
            background: resolveEmphasisBackground({ ...style, emphasisBgOpacity: style?.emphasisBgOpacity ?? 28 }),
            borderBottom: `2px solid ${style?.emphasisColor || '#ffffff'}`,
            padding: '0 0.08em',
            borderRadius: '4px',
        },
    };

    return {
        ...baseTokenStyle,
        ...(presetStyleMap[preset] || {}),
        // Always apply word BG when emphasisBgOpacity > 0 (regardless of preset).
        ...(Number(style?.emphasisBgOpacity || 0) > 0
            ? { background: resolveEmphasisBackground(style), borderRadius: '4px', padding: '0 0.12em' }
            : {}),
        // Always allow custom stroke/shadow to layer with preset when set explicitly.
        // Stroke stored as 0-20 percentage; converted to em for proportional sizing.
        ...(Number(style?.emphasisStrokeWidth || 0) > 0
            ? { WebkitTextStroke: `${Number(style?.emphasisStrokeWidth) * 0.008}em ${style?.emphasisStrokeColor || '#000000'}` }
            : {}),
        // Shadow applies whenever blur or opacity imply a visible shadow.
        ...((Number(style?.emphasisShadowBlur ?? 8) > 0 || Number(style?.emphasisShadowOpacity ?? 55) > 0)
            ? { textShadow: resolveEmphasisShadow(style) }
            : {}),
    };
};

// Builds an even-split per-word timing window when no ASR word-level timestamps
// (action.style.wordTimings) are available. Each word gets a proportional slice of the
// cue's [start, end] range, weighted by character length so longer words hold longer.
const buildEvenSplitWordWindows = (words, action) => {
    const start = Number(action?.start ?? 0);
    const end = Number(action?.end ?? start);
    const duration = Math.max(0, end - start);
    const totalChars = words.reduce((sum, w) => sum + Math.max(1, w.length), 0) || 1;

    let cursor = 0;
    return words.map((word) => {
        const weight = Math.max(1, word.length) / totalChars;
        const windowDuration = duration * weight;
        const windowStart = cursor;
        cursor += windowDuration;
        return { startMs: windowStart * 1000, endMs: cursor * 1000 };
    });
};

// Resolves per-word timing windows, preferring explicit ASR word timings (action.style.wordTimings,
// as [{word, startMs, endMs}]) when present and falling back to an even character-weighted split.
const resolveWordTimingWindows = (words, action) => {
    const explicit = action?.style?.wordTimings;
    if (Array.isArray(explicit) && explicit.length === words.length) {
        const start = Number(action?.start ?? 0) * 1000;
        return explicit.map((w) => ({
            startMs: Math.max(0, Number(w?.startMs ?? 0) - start),
            endMs: Math.max(0, Number(w?.endMs ?? 0) - start),
        }));
    }
    return buildEvenSplitWordWindows(words, action);
};

// Computes the opacity/transform for a single word's entrance/exit animation, using the same
// presets as getAnimationPreviewStyle (fade/pop/slideUp) but staggered per-word via its own
// timing window instead of applying uniformly to the whole subtitle block.
const getWordAnimationStyle = (window, action, currentTime) => {
    const style = action?.style || {};
    const animationIn = style.textAnimationIn ?? defaultStyle.textAnimationIn;
    const animationOut = style.textAnimationOut ?? defaultStyle.textAnimationOut;
    const { inMs, outMs } = resolveAnimationDurations(style);
    const cueStart = Number(action?.start ?? 0);
    const cueEnd = Number(action?.end ?? cueStart);
    const elapsedMs = Math.max(0, (Number(currentTime ?? cueStart) - cueStart)) * 1000;
    const cueDurationMs = Math.max(0, (cueEnd - cueStart) * 1000);
    const remainingMs = Math.max(0, cueDurationMs - elapsedMs);

    // "in" phase: word fades/pops in once the playhead reaches its own window start.
    if (inMs > 0 && elapsedMs < window.startMs + inMs) {
        if (elapsedMs < window.startMs) {
            return animationIn === 'none' ? { opacity: 1, transform: '' } : { opacity: 0, transform: '' };
        }
        const progress = clamp((elapsedMs - window.startMs) / inMs, 0, 1);
        return applyAnimationPreset(animationIn, progress);
    }
    // "out" phase: mirror the stagger near the end of the cue, keyed off remaining time.
    if (outMs > 0 && remainingMs < outMs) {
        const progress = clamp(remainingMs / outMs, 0, 1);
        return applyAnimationPreset(animationOut, progress);
    }
    return { opacity: 1, transform: '' };
};

const applyAnimationPreset = (preset, progress) => {
    if (preset === 'none') {
        return { opacity: 1, transform: '' };
    }
    if (preset === 'pop') {
        return { opacity: 1, transform: `scale(${Math.max(0.001, progress)})` };
    }
    if (preset === 'slideUp') {
        const translateY = (1 - progress) * 40;
        return { opacity: progress, transform: `translateY(${translateY}px)` };
    }
    return { opacity: progress, transform: '' };
};

// Renders subtitle text as per-word spans, applying both the static "Styled Words" emphasis
// look and (when `animationContext` is supplied) a per-word entrance/exit animation staggered
// across the cue's timing window — so fade/pop/slideUp play out word-by-word rather than as a
// single whole-block fade.
const renderTextWithWordEmphasis = (text, style, animationContext) => {
    const content = text || '';
    const emphasisWords = String(style?.emphasisWords || '')
        .split(',')
        .map((word) => normalizeToken(word))
        .filter(Boolean);
    const emphasizedSet = new Set(emphasisWords);
    const tokenStyle = buildEmphasisTokenStyle(style);

    const parts = content.split(/(\s+)/);
    const wordIndices = [];
    parts.forEach((part, index) => {
        if (part.trim()) wordIndices.push(index);
    });

    const animationIn = style?.textAnimationIn ?? defaultStyle.textAnimationIn;
    const shouldAnimatePerWord = Boolean(animationContext?.action) && animationIn !== 'none';
    const windows = shouldAnimatePerWord
        ? resolveWordTimingWindows(wordIndices.map((i) => parts[i]), animationContext.action)
        : null;

    let wordCursor = 0;
    return parts.map((part, index) => {
        const key = `${part}-${index}`;
        if (!part.trim()) {
            return <span key={key}>{part}</span>;
        }

        const wordPosition = wordCursor;
        wordCursor += 1;

        const shouldEmphasize = emphasizedSet.has(normalizeToken(part));
        const combinedStyle = shouldEmphasize ? { ...tokenStyle } : {};

        if (windows) {
            // inline-block is only needed to let transform (scale/translateY) apply per-word;
            // non-animated words keep the original bare-span layout to avoid regressing wrapping.
            combinedStyle.display = 'inline-block';
            const anim = getWordAnimationStyle(windows[wordPosition], animationContext.action, animationContext.currentTime);
            combinedStyle.opacity = anim.opacity;
            combinedStyle.transform = anim.transform || undefined;
        }

        if (!shouldEmphasize && !windows) {
            return <span key={key}>{part}</span>;
        }

        return (
            <span
                key={key}
                style={combinedStyle}
            >
                {part}
            </span>
        );
    });
};

// Resolves the effective in/out animation duration for a cue, falling back to the legacy
// fadeInMs/fadeOutMs fields so cues saved before textAnimationIn/animationDurationMs existed
// keep rendering exactly as before.
const resolveAnimationDurations = (style) => {
    const hasDurationMs = style?.animationDurationMs !== undefined && style?.animationDurationMs !== null;
    const durationMs = hasDurationMs ? Number(style.animationDurationMs) : null;
    const inMs = Number.isFinite(durationMs) ? durationMs : Number(style?.fadeInMs ?? defaultStyle.fadeInMs);
    const outMs = Number.isFinite(durationMs) ? durationMs : Number(style?.fadeOutMs ?? defaultStyle.fadeOutMs);
    return {
        inMs: Math.max(0, inMs || 0),
        outMs: Math.max(0, outMs || 0),
    };
};

// Derives { elapsed, phase, progress } for a cue at the given playhead time. `progress` is
// 0..1 within the current phase — callers drive opacity/transform off it. Cues with no active
// window return a fully-visible "hold" state so callers can render statically outside the player.
const calcAnimationProgress = (action, currentTime) => {
    const start = Number(action?.start ?? 0);
    const end = Number(action?.end ?? start);
    const duration = Math.max(0, end - start);
    const elapsedMs = Math.max(0, (Number(currentTime ?? start) - start)) * 1000;
    const { inMs, outMs } = resolveAnimationDurations(action?.style);
    const durationMs = duration * 1000;
    const remainingMs = Math.max(0, durationMs - elapsedMs);

    if (inMs > 0 && elapsedMs < inMs) {
        return { elapsed: elapsedMs, phase: 'in', progress: clamp(elapsedMs / inMs, 0, 1) };
    }
    if (outMs > 0 && remainingMs < outMs) {
        return { elapsed: elapsedMs, phase: 'out', progress: clamp(remainingMs / outMs, 0, 1) };
    }
    return { elapsed: elapsedMs, phase: 'hold', progress: 1 };
};

// Builds the CSS opacity/transform for the "in"/"out" whole-cue-box animation presets
// (fade, pop, slideUp). Karaoke/typewriter render their content differently and don't use this.
const getAnimationPreviewStyle = (action, currentTime) => {
    const style = action?.style || {};
    const animationIn = style.textAnimationIn ?? defaultStyle.textAnimationIn;
    const animationOut = style.textAnimationOut ?? defaultStyle.textAnimationOut;
    const { phase, progress } = calcAnimationProgress(action, currentTime);

    if (phase === 'hold') {
        return { opacity: 1, transform: '' };
    }

    const activePreset = phase === 'in' ? animationIn : animationOut;
    // Fade is symmetric in both phases: opacity ramps with progress on the way in,
    // and ramps back down (1 - progress is not used here since progress already represents
    // "how much of the out-phase remains", i.e. 1 = just started, 0 = fully hidden).
    if (activePreset === 'none') {
        return { opacity: 1, transform: '' };
    }
    if (activePreset === 'pop') {
        return { opacity: 1, transform: `scale(${Math.max(0.001, progress)})` };
    }
    if (activePreset === 'slideUp') {
        const translateY = (1 - progress) * 40;
        return { opacity: progress, transform: `translateY(${translateY}px)` };
    }
    // 'fade', 'karaoke', 'typewriter' (whose whole-box entrance still fades) all fall back to opacity-only.
    return { opacity: progress, transform: '' };
};

const extractSubtitleActions = (timelineObj) => {
    if (!timelineObj || typeof timelineObj !== 'object') return [];

    if (Array.isArray(timelineObj.editorData) && timelineObj.editorData[0]?.actions) {
        return timelineObj.editorData[0].actions;
    }

    const tracks = Array.isArray(timelineObj.tracks) ? timelineObj.tracks : [];
    const subtitleTrack = tracks.find((t) => t?.type === 'subtitle') || tracks[0];
    if (!subtitleTrack) return [];

    if (Array.isArray(subtitleTrack.actions)) {
        return subtitleTrack.actions.map((a, idx) => ({
            ...a,
            id: a.id || `subtitle-${idx + 1}`,
            effectId: a.effectId || 'subtitle',
            style: { ...defaultStyle, ...(a.style || {}) },
            text: a.text || '',
        }));
    }

    if (Array.isArray(subtitleTrack.elements)) {
        return subtitleTrack.elements.map((e, idx) => {
            const start = Number(e.start ?? e.startTime ?? e.startSec ?? 0);
            const end = Number(e.end ?? e.endTime ?? e.endSec ?? start + 2);
            return {
                id: e.id || `subtitle-${idx + 1}`,
                effectId: 'subtitle',
                start,
                end: Math.max(end, start + MIN_ACTION_DURATION),
                movable: true,
                flexible: true,
                text: e.text || e.content || '',
                style: { ...defaultStyle, ...(e.style || {}) },
            };
        });
    }

    if (Array.isArray(subtitleTrack.clips)) {
        return subtitleTrack.clips.map((clip, idx) => {
            const start = Number((clip.startMs || 0) / 1000);
            const end = Number((clip.endMs || clip.outMs || 0) / 1000);
            return {
                id: clip.id || `subtitle-${idx + 1}`,
                effectId: 'subtitle',
                start,
                end: Math.max(end, start + MIN_ACTION_DURATION),
                movable: true,
                flexible: true,
                text: clip.text || clip.value || clip.cueRef || '',
                style: { ...defaultStyle, ...(clip.style || {}) },
            };
        });
    }

    return [];
};

const pickLatestSrtSourceHash = (sourceList) => {
    const list = Array.isArray(sourceList) ? sourceList : [];
    if (list.length === 0) return '';

    // API is expected to return latest-first for this endpoint; prefer SRT entries within that order.
    const srtSource = list.find((source) => {
        const sourceType = String(source?.sourceType || '').toLowerCase();
        const fileUrl = String(source?.fileUrl || '').toLowerCase();
        return sourceType.includes('srt') || fileUrl.endsWith('.srt');
    });

    return srtSource?.sourceHash || list[0]?.sourceHash || '';
};

const pickWordSpecificStyle = (style) => {
    const next = {};
    WORD_SPECIFIC_STYLE_KEYS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(style || {}, key)) {
            next[key] = style[key];
        }
    });
    return next;
};

const omitWordSpecificStyle = (style) => {
    const next = { ...(style || {}) };
    WORD_SPECIFIC_STYLE_KEYS.forEach((key) => {
        delete next[key];
    });
    return next;
};

const normalizeCueStyleForPersistence = (style) => {
    const merged = { ...defaultStyle, ...(style || {}) };
    return {
        ...merged,
        emphasisWords: String(merged.emphasisWords || ''),
    };
};

const normalizeActionsForPersistence = (actions) => {
    const list = Array.isArray(actions) ? actions : [];
    return list.map((action) => ({
        ...action,
        style: normalizeCueStyleForPersistence(action?.style),
    }));
};

// Builds the same export payload shape as handleExportVideo, but purely from a
// persisted ReelProject record (timelineDataJson/subtitleOverridesJson) rather than
// live editor state. Used for exporting projects that are not currently open in the
// editor (bulk export). previewW/previewH mirror the fallback the live export path
// already takes when there is no mounted preview DOM to measure.
const buildExportPayloadForProject = (project) => {
    const selectedFormat = platformPresets[project?.reelFormat] || platformPresets.vertical;

    const timelineObj = JSON.parse(project?.timelineDataJson || '{}');
    const overridesObj = JSON.parse(project?.subtitleOverridesJson || '{}');
    const overlayFromTimeline = timelineObj?.overlaySettings || {};
    const overlayFromOverrides = overridesObj?.overlaySettings || {};

    const shapeOverlay = {
        ...defaultShapeOverlay,
        ...(overlayFromTimeline?.shapeOverlay || {}),
        ...(overlayFromOverrides?.shapeOverlay || {}),
    };
    const logoOverlay = {
        ...defaultLogoOverlay,
        ...(overlayFromTimeline?.logoOverlay || {}),
        ...(overlayFromOverrides?.logoOverlay || {}),
    };

    const actions = extractSubtitleActions(timelineObj).map((action) => ({
        ...action,
        style: {
            ...defaultStyle,
            ...(overridesObj?.globalStyle || {}),
            ...(action.style || {}),
            ...(overridesObj?.cueOverrides?.[action.id]?.style || {}),
        },
    }));

    const previewW = 460;
    const previewH = Math.round(previewW * selectedFormat.height / Math.max(1, selectedFormat.width));
    const exportScaleX = selectedFormat.width / previewW;
    const exportScaleY = selectedFormat.height / previewH;

    const exportCues = actions
        .map((action) => {
            const start = Number(action?.start);
            const end = Number(action?.end);
            if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
            const rawStyle = normalizeCueStyleForPersistence(action?.style);
            return {
                start,
                end,
                text: String(action?.text || ''),
                style: {
                    ...rawStyle,
                    fontSize: Math.round(rawStyle.fontSize * exportScaleY),
                    offsetX: Math.round(rawStyle.offsetX * exportScaleX),
                    offsetY: Math.round(rawStyle.offsetY * exportScaleY),
                    boxPaddingX: Math.round((rawStyle.boxPaddingX || 14) * exportScaleX),
                    boxPaddingY: Math.round((rawStyle.boxPaddingY || 8) * exportScaleY),
                    textShadowOffsetX: rawStyle.textShadowOffsetX || 0,
                    textShadowOffsetY: rawStyle.textShadowOffsetY || 0,
                    textShadowBlur: rawStyle.textShadowBlur || 0,
                    textShadowColor: rawStyle.textShadowColor || '#000000',
                    textShadowOpacity: rawStyle.textShadowOpacity || 0,
                    emphasisShadowBlur: rawStyle.emphasisShadowBlur ?? 8,
                    emphasisShadowOffsetY: rawStyle.emphasisShadowOffsetY ?? 2,
                    emphasisStrokeWidth: rawStyle.emphasisStrokeWidth,
                    _anchorY: Math.round(SUBTITLE_ANCHOR_MARGIN_Y * exportScaleY),
                    _anchorX: Math.round(SUBTITLE_ANCHOR_MARGIN_X * exportScaleX),
                },
            };
        })
        .filter(Boolean);

    const subtitleContent = buildSrtFromActions(actions);
    if (!subtitleContent) return null;

    const styleForExport = {
        ...defaultStyle,
        ...(overridesObj?.globalStyle || {}),
    };

    const globalShapeForExport = shapeOverlay?.enabled ? { ...shapeOverlay } : {};
    const globalImageForExport = logoOverlay?.enabled ? { ...logoOverlay } : {};

    return {
        videoId: project.videoId,
        language: project.languageName,
        subtitleFormat: 'srt',
        subtitleContent,
        renderManifest: {
            cues: exportCues,
            globalShape: globalShapeForExport,
            globalImage: globalImageForExport,
            videoFadeInMs: timelineObj?.tracks?.[0]?.clips?.[0]?.transitionIn?.durationMs ?? 200,
            videoFadeOutMs: timelineObj?.tracks?.[0]?.clips?.[0]?.transitionOut?.durationMs ?? 220,
            muteVideoAudio: false,
        },
        format: {
            name: selectedFormat.name,
            aspectRatio: selectedFormat.ratio,
            resolution: `${selectedFormat.width}x${selectedFormat.height}`,
        },
        subtitleStyle: {
            fontFamily: styleForExport.fontFamily,
            fontSize: Number(styleForExport.fontSize || 44),
            fontColor: styleForExport.color || '#ffffff',
            bold: Boolean(styleForExport.bold),
            italic: Boolean(styleForExport.italic),
            underline: Boolean(styleForExport.underline),
            backgroundColor: styleForExport.backgroundColor || '#000000',
            backgroundOpacity: Math.max(0, Math.min(1, Number(styleForExport.backgroundOpacity || 0) / 100)),
            position: styleForExport.position || 'bottom-center',
            boxPadding: Number(styleForExport.boxPaddingY || 8),
            boxMargin: Math.max(0, Math.abs(Number(styleForExport.offsetY || 0))),
        },
    };
};

const ReelEditorPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    useTrackUsage('ReelEditorPage', 'editor', 'reels');
    const { videoId: routeVideoId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const timelineRef = useRef(null);
    const subtitleListRef = useRef(null);
    const videoPreviewRef = useRef(null);
    const dragStateRef = useRef(null);
    const resizeStateRef = useRef(null);
    const overlayDragStateRef = useRef(null);
    const autoHydratePauseRef = useRef(false);
    const latestRowsRef = useRef([{ id: 'subtitle-row-main', actions: [] }]);
    // When the user clicks a subtitle (chip or block), we set this flag for two
    // animation frames to ignore the spurious onCursorDrag(0) the timeline library
    // fires when it resets its internal cursor during a re-render.
    const selectionLockedRef = useRef(false);
    const autoSaveTimerRef = useRef(null);
    const hasAutoLoadedRef = useRef(false);
    const hasAutoCreatedRef = useRef(false);

    const preloadedVideoData = useMemo(() => {
        const stateVideoData = location.state?.videoData;
        if (stateVideoData) return stateVideoData;
        return JSON.parse(localStorage.getItem('videoProps') || '{}');
    }, [location.state]);

    const availableLanguages = useMemo(() => {
        if (Array.isArray(preloadedVideoData?.availableLanguages) && preloadedVideoData.availableLanguages.length > 0) {
            return preloadedVideoData.availableLanguages;
        }
        const langs = (preloadedVideoData?.videoLanguages || []).map((l) => l?.languageName).filter(Boolean);
        if (langs.length > 0) return Array.from(new Set(langs));
        return ['English'];
    }, [preloadedVideoData]);

    const [videoId, setVideoId] = useState(routeVideoId || preloadedVideoData?.id || '');
    const [languageName, setLanguageName] = useState(availableLanguages[0] || 'English');
    const [projectName, setProjectName] = useState('');
    const [sources, setSources] = useState([]);
    const [selectedSourceHash, setSelectedSourceHash] = useState('');
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [reelFormat, setReelFormat] = useState('vertical');

    const [fetchedVideoData, setFetchedVideoData] = useState(null);
    const [loadingSources, setLoadingSources] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportStatus, setExportStatus] = useState(null);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [loadingProject, setLoadingProject] = useState(false);
    const [message, setMessage] = useState(null);
    const [videoLoadError, setVideoLoadError] = useState('');
    const [projectTimelineVideoUrl, setProjectTimelineVideoUrl] = useState('');
    const [showAdvancedControls, setShowAdvancedControls] = useState(false);
    const [showHelpPanel, setShowHelpPanel] = useState(false);
    const [bulkExportOpen, setBulkExportOpen] = useState(false);
    const [autoCreateChecked, setAutoCreateChecked] = useState(false);
    const [loadedProjectId, setLoadedProjectId] = useState('');
    const [sharedLanguageStyle, setSharedLanguageStyle] = useState(null);
    const [shapeOverlay, setShapeOverlay] = useState(defaultShapeOverlay);
    const [logoOverlay, setLogoOverlay] = useState(defaultLogoOverlay);

    const [editorData, setEditorData] = useState([
        {
            id: 'subtitle-row-main',
            actions: [],
        },
    ]);

    const [, setTimelineDataJson] = useState('{}');
    const [, setSubtitleOverridesJson] = useState('{}');

    const [selectedActionId, setSelectedActionId] = useState('');
    // Stable ref so effects that only care about editorData changes can still
    // read the current selectedActionId without having it as a dep (which would
    // cause the auto-select reset loop described below).
    const selectedActionIdRef = useRef('');
    const [currentTime, setCurrentTime] = useState(0);
    const [videoFadeInMs, setVideoFadeInMs] = useState(200);
    const [videoFadeOutMs, setVideoFadeOutMs] = useState(220);
    const [isDraggingSubtitle, setIsDraggingSubtitle] = useState(false);
    const [isResizingSubtitle, setIsResizingSubtitle] = useState(false);
    const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
    const [shapeOverlayScope, setShapeOverlayScope] = useState('local');
    const [imageOverlayScope, setImageOverlayScope] = useState('local');
    const [showSubtitleStyle, setShowSubtitleStyle] = useState(true);
    const [showWordEmphasis, setShowWordEmphasis] = useState(true);
    const [showShapeOverlay, setShowShapeOverlay] = useState(true);
    const [showImageOverlay, setShowImageOverlay] = useState(true);
    const [preserveWordStylesOnApplyAll, setPreserveWordStylesOnApplyAll] = useState(true);
    // Scope for "Apply Selected Style To All": 'current' | 'language' | 'resolution' | 'all'
    const [applyStyleScope, setApplyStyleScope] = useState('current');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // ── Audio track state ────────────────────────────────────────────────────
    const audioRef = useRef(null);
    const dubbedAudioInputRef = useRef(null);
    const audioStateByLangRef = useRef({});   // persists audio state per language within the session
    const prevLanguageNameRef = useRef('');
    const [muteVideoAudio, setMuteVideoAudio] = useState(false);
    const [dubbedAudioUrl, setDubbedAudioUrl] = useState('');
    const [dubbedAudioDuration, setDubbedAudioDuration] = useState(0);
    const [audioSegments, setAudioSegments] = useState([]);
    const [selectedAudioSegmentId, setSelectedAudioSegmentId] = useState('');
    // ────────────────────────────────────────────────────────────────────────

    const selectedSource = useMemo(
        () => sources.find((source) => source.sourceHash === selectedSourceHash) || null,
        [sources, selectedSourceHash],
    );

    const videoUrl = useMemo(() => {
        const fetchedUrl = extractVideoUrlFromUnknown(fetchedVideoData);
        const preloadedUrl = extractVideoUrlFromUnknown(preloadedVideoData);
        return fetchedUrl || preloadedUrl || projectTimelineVideoUrl || '';
    }, [fetchedVideoData, preloadedVideoData, projectTimelineVideoUrl]);

    const selectedAction = useMemo(() => {
        return editorData[0]?.actions?.find((a) => a.id === selectedActionId) || null;
    }, [editorData, selectedActionId]);

    const seekToSubtitleAction = useCallback((action) => {
        if (!action?.id) return;
        const nextTime = Number(action.start || 0);
        // Lock out spurious onCursorDrag callbacks the library fires when it resets
        // its cursor to 0 on re-render (triggered by selectedActionId state change).
        selectionLockedRef.current = true;
        setSelectedActionId(action.id);
        if (Number.isFinite(nextTime)) {
            setCurrentTime(nextTime);
            if (videoPreviewRef.current) {
                videoPreviewRef.current.currentTime = nextTime;
            }
            if (timelineRef.current?.setTime) {
                timelineRef.current.setTime(nextTime);
            }
            // Re-apply after the re-render so the restored position wins.
            requestAnimationFrame(() => {
                if (timelineRef.current?.setTime) {
                    timelineRef.current.setTime(nextTime);
                }
                // Unlock after a second frame — library should be settled by then.
                requestAnimationFrame(() => { selectionLockedRef.current = false; });
            });
        } else {
            requestAnimationFrame(() => { requestAnimationFrame(() => { selectionLockedRef.current = false; }); });
        }
    }, []);

    // ── Dubbed-audio handlers ────────────────────────────────────────────────
    const handleDubbedAudioUpload = useCallback((file) => {
        if (!file) return;
        // Revoke any previous object URL to avoid memory leaks.
        if (dubbedAudioUrl) URL.revokeObjectURL(dubbedAudioUrl);
        const url = URL.createObjectURL(file);
        setDubbedAudioUrl(url);
        // Load metadata to get duration, then set the initial full-width segment.
        const tempAudio = new Audio(url);
        tempAudio.addEventListener('loadedmetadata', () => {
            const dur = tempAudio.duration || 0;
            setDubbedAudioDuration(dur);
            setAudioSegments([{ id: 'audio-seg-0', start: 0, end: dur, effectId: 'audio', movable: true, flexible: true, audioOffset: 0, label: file.name }]);
        }, { once: true });
    }, [dubbedAudioUrl]);

    const handleSplitAudioSegment = useCallback(() => {
        if (!selectedAudioSegmentId) return;
        setAudioSegments((prev) => {
            const idx = prev.findIndex((s) => s.id === selectedAudioSegmentId);
            if (idx === -1) return prev;
            const seg = prev[idx];
            if (currentTime <= seg.start || currentTime >= seg.end) return prev;
            const splitPoint = currentTime;
            const first = { ...seg, end: splitPoint };
            const second = {
                id: `audio-seg-${Date.now()}`,
                start: splitPoint,
                end: seg.end,
                effectId: 'audio',
                movable: true,
                flexible: true,
                audioOffset: seg.audioOffset + (splitPoint - seg.start),
                label: seg.label,
            };
            const next = [...prev];
            next.splice(idx, 1, first, second);
            return next;
        });
    }, [selectedAudioSegmentId, currentTime]);

    const handleClearAudio = useCallback(() => {
        if (dubbedAudioUrl) URL.revokeObjectURL(dubbedAudioUrl);
        setDubbedAudioUrl('');
        setDubbedAudioDuration(0);
        setAudioSegments([]);
        setSelectedAudioSegmentId('');
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
        }
        // Remove from per-language cache so switching back doesn't restore stale state.
        delete audioStateByLangRef.current[languageName];
    }, [dubbedAudioUrl, languageName]);
    // ────────────────────────────────────────────────────────────────────────

    // Save/restore audio state when the active language changes so each language keeps its own audio track.
    useEffect(() => {
        const prevLang = prevLanguageNameRef.current;
        const newLang = languageName;
        if (prevLang === newLang) return;

        // Persist current state for the language we're leaving.
        if (prevLang) {
            audioStateByLangRef.current[prevLang] = {
                muteVideoAudio,
                dubbedAudioUrl,
                dubbedAudioDuration,
                audioSegments,
            };
        }
        prevLanguageNameRef.current = newLang;

        const saved = audioStateByLangRef.current[newLang];
        setMuteVideoAudio(saved?.muteVideoAudio ?? false);
        setDubbedAudioUrl(saved?.dubbedAudioUrl ?? '');
        setDubbedAudioDuration(saved?.dubbedAudioDuration ?? 0);
        setAudioSegments(saved?.audioSegments ?? []);
        setSelectedAudioSegmentId('');

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = saved?.dubbedAudioUrl ?? '';
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languageName]);

    const effects = useMemo(() => ({
        subtitle: {
            id: 'subtitle',
            name: 'Subtitle',
        },
        audio: {
            id: 'audio',
            name: 'Audio',
        },
    }), []);

    // Combines subtitle editorData with the audio row (if dubbed audio is loaded).
    const timelineRows = useMemo(() => {
        if (dubbedAudioUrl && audioSegments.length > 0) {
            return [...editorData, { id: 'audio-row-main', actions: audioSegments }];
        }
        return editorData;
    }, [editorData, dubbedAudioUrl, audioSegments]);

    const autoProjectName = useMemo(() => {
        const videoName = preloadedVideoData?.videoName || preloadedVideoData?.name || videoId || 'Untitled';
        const formatName = platformPresets[reelFormat]?.name || reelFormat;
        return `${videoName} - ${languageName} - ${formatName}`;
    }, [preloadedVideoData, videoId, languageName, reelFormat]);

    useEffect(() => {
        if (projectName !== autoProjectName) {
            setProjectName(autoProjectName);
        }
    }, [autoProjectName, projectName]);

    useEffect(() => {
        if (!videoId) {
            setSharedLanguageStyle(null);
            return;
        }

        const key = `reelSharedStyle:${videoId}`;
        try {
            const raw = localStorage.getItem(key);
            if (!raw) {
                setSharedLanguageStyle(null);
                return;
            }
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
                setSharedLanguageStyle(parsed);
            }
        } catch (error) {
            setSharedLanguageStyle(null);
        }
    }, [videoId]);

    useEffect(() => {
        if (!videoId) return;
        const key = `reelSharedStyle:${videoId}`;
        try {
            if (!sharedLanguageStyle) {
                localStorage.removeItem(key);
                return;
            }
            localStorage.setItem(key, JSON.stringify(sharedLanguageStyle));
        } catch (error) {
            // Ignore persistence errors in style cache.
        }
    }, [sharedLanguageStyle, videoId]);

    // Sync video mute state whenever muteVideoAudio changes.
    useEffect(() => {
        if (videoPreviewRef.current) {
            videoPreviewRef.current.muted = muteVideoAudio;
        }
    }, [muteVideoAudio]);

    // Sync dubbed audio playback with the video element.
    useEffect(() => {
        const videoEl = videoPreviewRef.current;
        const audioEl = audioRef.current;
        if (!videoEl || !audioEl || !dubbedAudioUrl) return;

        audioEl.src = dubbedAudioUrl;

        const getActiveSegment = (videoTime) =>
            audioSegments.find((s) => videoTime >= s.start && videoTime < s.end);

        const syncTime = (videoTime) => {
            const seg = getActiveSegment(videoTime);
            if (seg) {
                const expected = seg.audioOffset + (videoTime - seg.start);
                if (Math.abs(audioEl.currentTime - expected) > 0.15) {
                    audioEl.currentTime = expected;
                }
            }
        };

        const onPlay = () => {
            syncTime(videoEl.currentTime);
            const seg = getActiveSegment(videoEl.currentTime);
            if (seg) audioEl.play().catch(() => {});
        };
        const onPause = () => { audioEl.pause(); };
        const onSeeked = () => { syncTime(videoEl.currentTime); };
        const onTimeUpdate = () => {
            syncTime(videoEl.currentTime);
            if (!getActiveSegment(videoEl.currentTime)) audioEl.pause();
        };

        videoEl.addEventListener('play', onPlay);
        videoEl.addEventListener('pause', onPause);
        videoEl.addEventListener('seeked', onSeeked);
        videoEl.addEventListener('timeupdate', onTimeUpdate);
        return () => {
            videoEl.removeEventListener('play', onPlay);
            videoEl.removeEventListener('pause', onPause);
            videoEl.removeEventListener('seeked', onSeeked);
            videoEl.removeEventListener('timeupdate', onTimeUpdate);
            audioEl.pause();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dubbedAudioUrl, audioSegments]);

    // Revoke object URL when dubbed audio is cleared.
    useEffect(() => {
        return () => {
            if (dubbedAudioUrl) URL.revokeObjectURL(dubbedAudioUrl);
        };
    // We only want the cleanup to run when the URL changes or on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dubbedAudioUrl]);

    // Sync timeline playhead cursor to video current time.
    // Also re-sync whenever timelineRows, selectedActionId, or selectedAudioSegmentId changes
    // because the @xzdarcy/react-timeline-editor library resets the cursor to 0 on re-render
    // (triggered by any prop change to the Timeline component including selection state).
    useEffect(() => {
        if (timelineRef.current?.setTime) {
            timelineRef.current.setTime(currentTime);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime, timelineRows, selectedActionId, selectedAudioSegmentId]);

    // Scroll the timeline and subtitle blocks list whenever the selected subtitle changes.
    useEffect(() => {
        if (!selectedActionId) return;
        // Timeline: keep selected chip in the 20–80 % visible band.
        const selectedAction = editorData?.[0]?.actions?.find((a) => a.id === selectedActionId);
        if (selectedAction) timelineRef.current?.scrollToTime?.(selectedAction.start);
        // Subtitle blocks list: keep active item in the 20–80 % visible band.
        const activeItem = subtitleListRef.current?.querySelector(`[data-action-id="${selectedActionId}"]`);
        if (activeItem && subtitleListRef.current) {
            const container = subtitleListRef.current;
            const containerRect = container.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();
            const containerHeight = container.clientHeight;
            const currentScrollTop = container.scrollTop;
            // offsetTop relative to the scroll container (not the page)
            const itemTopRelative = itemRect.top - containerRect.top + currentScrollTop;
            const itemHeight = activeItem.offsetHeight;
            const rangeStart = currentScrollTop + containerHeight * 0.2;
            const rangeEnd   = currentScrollTop + containerHeight * 0.8;
            if (itemTopRelative < rangeStart || itemTopRelative + itemHeight > rangeEnd) {
                const desired = Math.max(0, itemTopRelative - containerHeight * 0.3);
                container.scrollTo({ top: desired, behavior: 'smooth' });
            }
        }
    // editorData intentionally omitted — we only want to scroll on selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedActionId]);

    // Track the selected subtitle as the video plays or is seeked.
    // Uses refs (not state deps) to avoid re-triggering hydrateFromSource or other effects.
    useEffect(() => {
        const actions = editorDataRef.current?.[0]?.actions || [];
        const matchingAction = actions.find((a) => currentTime >= a.start && currentTime < a.end);
        // Only update when crossing a subtitle boundary — not on every video frame.
        if (matchingAction && matchingAction.id !== selectedActionIdRef.current) {
            setSelectedActionId(matchingAction.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime]);

    const makeTimelinePayload = useCallback(
        (rows) => {
            const actions = normalizeActionsForPersistence(rows?.[0]?.actions || []);
            const maxEnd = actions.reduce((acc, action) => Math.max(acc, action.end || 0), 0);
            const durationMs = Math.max(4000, Math.ceil(maxEnd * 1000));

            return {
                version: 1,
                fps: 30,
                durationMs,
                overlaySettings: {
                    shapeOverlay,
                    logoOverlay,
                },
                tracks: [
                    {
                        id: 'video-main',
                        type: 'video',
                        clips: [
                            {
                                id: 'video-clip-1',
                                src: videoUrl || '',
                                inMs: 0,
                                outMs: durationMs,
                                startMs: 0,
                                transitionIn: { type: 'fade', durationMs: videoFadeInMs },
                                transitionOut: { type: 'fade', durationMs: videoFadeOutMs },
                            },
                        ],
                    },
                    {
                        id: 'subtitle-main',
                        type: 'subtitle',
                        sourceHash: selectedSource?.sourceHash || '',
                        actions,
                    },
                ],
                editorData: rows,
            };
        },
        [videoFadeInMs, videoFadeOutMs, selectedSource, videoUrl, shapeOverlay, logoOverlay],
    );

    const makeOverridesPayload = useCallback((rows) => {
        const actions = normalizeActionsForPersistence(rows?.[0]?.actions || []);
        const cueOverrides = {};
        actions.forEach((action) => {
            cueOverrides[action.id] = {
                text: action.text || '',
                style: normalizeCueStyleForPersistence(action.style),
                start: action.start,
                end: action.end,
            };
        });

        const selectedActionStyle = actions.find((action) => action.id === selectedActionIdRef.current)?.style;
        const firstActionStyle = actions[0]?.style;
        const resolvedGlobalStyle = normalizeCueStyleForPersistence(
            selectedActionStyle || firstActionStyle || sharedLanguageStyle || defaultStyle,
        );

        return {
            globalStyle: resolvedGlobalStyle,
            overlaySettings: {
                shapeOverlay,
                logoOverlay,
            },
            cueOverrides,
        };
    }, [shapeOverlay, logoOverlay, sharedLanguageStyle]);

    const syncPayloadJson = useCallback(
        (rows) => {
            const timelinePayload = makeTimelinePayload(rows);
            const overridesPayload = makeOverridesPayload(rows);
            setTimelineDataJson(JSON.stringify(timelinePayload, null, 2));
            setSubtitleOverridesJson(JSON.stringify(overridesPayload, null, 2));
        },
        [makeTimelinePayload, makeOverridesPayload],
    );

    useEffect(() => {
        latestRowsRef.current = editorData;
    }, [editorData]);

    const loadVideoData = useCallback(async () => {
        if (!videoId) return;

        try {
            const response = await videoCRUDServiceInstance.getVideoById(videoId);
            const nextData = response?.data || null;
            setFetchedVideoData(nextData);
            setVideoLoadError('');

            const resolvedUrl = extractVideoUrlFromUnknown(nextData);
            if (!resolvedUrl) {
                setMessage({
                    severity: 'warning',
                    text: 'Video metadata loaded, but no playable URL was found in API response. Trying project timeline URL fallback.',
                });
            }
        } catch (error) {
            console.error('Failed to fetch video metadata for reel editor:', error);
            setVideoLoadError('Failed to fetch video metadata from API.');
        }
    }, [videoId]);

    const loadSources = useCallback(async () => {
        if (!videoId) return;

        setLoadingSources(true);
        try {
            const fetchedSources = await reelProjectServiceInstance.getLatestSubtitleSources(videoId, languageName);
            const nextSources = Array.isArray(fetchedSources) ? fetchedSources : [];
            setSources(nextSources);
            setSelectedSourceHash((previousHash) => {
                if (nextSources.length === 0) {
                    return '';
                }
                const previousStillExists = nextSources.some((source) => source?.sourceHash === previousHash);
                if (previousHash && previousStillExists) {
                    return previousHash;
                }
                return pickLatestSrtSourceHash(nextSources);
            });
        } catch (error) {
            console.error('Failed to load subtitle sources:', error);
            setMessage({ severity: 'error', text: 'Could not load subtitle sources.' });
        } finally {
            setLoadingSources(false);
        }
    }, [videoId, languageName]);

    const loadProjects = useCallback(async () => {
        if (!videoId) return;

        setLoadingProjects(true);
        try {
            const list = await reelProjectServiceInstance.listProjects(videoId);
            setProjects(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error('Failed to load reel projects:', error);
        } finally {
            setLoadingProjects(false);
        }
    }, [videoId]);

    useEffect(() => {
        loadVideoData();
        loadSources();
        loadProjects();
    }, [loadVideoData, loadSources, loadProjects]);

    // On first open, if this video is missing any English project formats and an
    // English subtitle source is available, silently create the missing
    // (English, format) projects so the user lands on a ready-to-edit project
    // instead of an empty editor. Formats that already have an English project
    // (any casing) are left untouched — never duplicated.
    useEffect(() => {
        if (!videoId || hasAutoCreatedRef.current || loadingProjects) return;
        if (!availableLanguages.includes('English')) {
            hasAutoCreatedRef.current = true;
            setAutoCreateChecked(true);
            return;
        }

        const formatOrder = ['vertical', 'horizontal', 'square'];
        const existingEnglishFormats = new Set(
            projects
                .filter((p) => String(p?.languageName || '').trim().toLowerCase() === 'english')
                .map((p) => p.reelFormat),
        );
        const missingFormats = formatOrder.filter((format) => !existingEnglishFormats.has(format));
        if (missingFormats.length === 0) {
            hasAutoCreatedRef.current = true;
            setAutoCreateChecked(true);
            return;
        }

        hasAutoCreatedRef.current = true;

        (async () => {
            try {
                const englishSources = await reelProjectServiceInstance.getLatestSubtitleSources(videoId, 'English');
                const source = Array.isArray(englishSources)
                    ? englishSources.find((s) => s?.fileUrl) || null
                    : null;
                if (!source) return;

                const response = await fetch(source.fileUrl);
                if (!response.ok) return;
                const vttText = await response.text();
                const cues = parseVtt(vttText);
                const actions = cues.map((cue, idx) => makeSubtitleAction(cue, idx));
                const rows = [{ id: 'subtitle-row-main', actions }];

                const timelinePayload = makeTimelinePayload(rows);
                const overridesPayload = makeOverridesPayload(rows);
                const timelineDataJson = JSON.stringify(timelinePayload, null, 2);
                const subtitleOverridesJson = JSON.stringify(overridesPayload, null, 2);

                const basePayload = {
                    videoId,
                    languageName: 'English',
                    subtitleSourceType: source.sourceType || null,
                    subtitleSourceVersionId: source.versionId || null,
                    subtitleSourceFileUrl: source.fileUrl || null,
                    subtitleSourceProcessStage: source.processStage || null,
                    subtitleSourceHash: source.sourceHash || null,
                    subtitleApprovedBy: source.approvedBy || null,
                    subtitleApprovedAt: source.approvedAt || null,
                    timelineDataJson,
                    subtitleOverridesJson,
                };

                const videoName = preloadedVideoData?.videoName || preloadedVideoData?.name || videoId || 'Untitled';
                for (const format of missingFormats) {
                    const formatName = platformPresets[format]?.name || format;
                    // eslint-disable-next-line no-await-in-loop
                    await reelProjectServiceInstance.createProject({
                        ...basePayload,
                        projectName: `${videoName} - English - ${formatName}`,
                        reelFormat: format,
                    });
                }

                await loadProjects();
            } catch (error) {
                console.error('Failed to auto-create English reel projects:', error);
            } finally {
                setAutoCreateChecked(true);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId, loadingProjects, projects, availableLanguages]);

    const hydrateFromSource = useCallback(async (source = selectedSource, options = {}) => {
        const { silent = false } = options;

        if (!source?.fileUrl) {
            setMessage({ severity: 'warning', text: 'Select a subtitle source first.' });
            return;
        }

        try {
            const response = await fetch(source.fileUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch subtitle file: ${response.status}`);
            }
            const vttText = await response.text();
            const cues = parseVtt(vttText);
            const actions = cues.map((cue, idx) => {
                const base = makeSubtitleAction(cue, idx);
                if (!sharedLanguageStyle) return base;
                return {
                    ...base,
                    style: {
                        ...defaultStyle,
                        ...sharedLanguageStyle,
                        ...pickWordSpecificStyle(base.style),
                    },
                };
            });

            const rows = [
                {
                    id: 'subtitle-row-main',
                    actions,
                },
            ];
            latestRowsRef.current = rows;
            setEditorData(rows);
            setSelectedActionId(actions[0]?.id || '');
            syncPayloadJson(rows);
            if (!silent) {
                setMessage({ severity: 'success', text: `Loaded ${actions.length} subtitle blocks from source.` });
            }
        } catch (error) {
            console.error('Failed to parse subtitle source:', error);
            setMessage({ severity: 'error', text: 'Unable to load subtitle source file.' });
        }
    }, [selectedSource, sharedLanguageStyle, syncPayloadJson]);

    useEffect(() => {
        if (!selectedSourceHash || !selectedSource?.fileUrl) return;
        if (autoHydratePauseRef.current) return;
        if (loadedProjectId) return;

        hydrateFromSource(selectedSource, { silent: true });
    }, [selectedSourceHash, selectedSource, hydrateFromSource, loadedProjectId]);

    // Keep the ref in sync on every render so the effect below can read the
    // latest selectedActionId without taking it as a dependency.
    selectedActionIdRef.current = selectedActionId;

    useEffect(() => {
        const actions = editorData?.[0]?.actions || [];
        const currentId = selectedActionIdRef.current;
        if (actions.length === 0) {
            if (currentId) setSelectedActionId('');
            return;
        }
        const hasSelectedAction = actions.some((action) => action.id === currentId);
        if (!hasSelectedAction) {
            setSelectedActionId(actions[0].id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorData]);

    const loadProject = async (projectIdOverride = '') => {
        const targetProjectId = projectIdOverride || selectedProjectId;
        if (!targetProjectId) return false;

        setLoadingProject(true);
        autoHydratePauseRef.current = true;
        try {
            const project = await reelProjectServiceInstance.getProject(targetProjectId);
            if (!project) return false;

            setLanguageName(project.languageName || languageName);
            setReelFormat(project.reelFormat || 'vertical');
            setSelectedSourceHash(project.subtitleSourceHash || '');
            setProjectTimelineVideoUrl(extractVideoUrlFromTimelineJson(project.timelineDataJson));

            const timelineObj = JSON.parse(project.timelineDataJson || '{}');
            const overridesObj = JSON.parse(project.subtitleOverridesJson || '{}');
            const overlayFromTimeline = timelineObj?.overlaySettings || {};
            const overlayFromOverrides = overridesObj?.overlaySettings || {};

            setShapeOverlay({
                ...defaultShapeOverlay,
                ...(overlayFromTimeline?.shapeOverlay || {}),
                ...(overlayFromOverrides?.shapeOverlay || {}),
            });
            setLogoOverlay({
                ...defaultLogoOverlay,
                ...(overlayFromTimeline?.logoOverlay || {}),
                ...(overlayFromOverrides?.logoOverlay || {}),
            });

            const extractedActions = extractSubtitleActions(timelineObj).map((action) => ({
                ...action,
                style: {
                    ...defaultStyle,
                    ...(overridesObj?.globalStyle || {}),
                    ...(action.style || {}),
                    ...(overridesObj?.cueOverrides?.[action.id]?.style || {}),
                },
            }));

            const rows = [{ id: 'subtitle-row-main', actions: extractedActions }];
            latestRowsRef.current = rows;
            setEditorData(rows);
            setSelectedActionId(extractedActions[0]?.id || '');
            setTimelineDataJson(JSON.stringify(timelineObj, null, 2));
            setSubtitleOverridesJson(JSON.stringify(overridesObj, null, 2));
            setSelectedProjectId(targetProjectId);
            setLoadedProjectId(targetProjectId);

            return true;
        } catch (error) {
            console.error('Failed to load project:', error);
            setMessage({ severity: 'error', text: 'Could not load selected project.' });
            return false;
        } finally {
            autoHydratePauseRef.current = false;
            setLoadingProject(false);
        }
    };

    const handleProjectSelectionChange = async (event) => {
        const nextProjectId = event.target.value;
        if (nextProjectId === '__new__') {
            setSelectedProjectId('');
            setLoadedProjectId('');
            setProjectName(autoProjectName);
            setMessage({ severity: 'info', text: 'Ready for a new project. Click Save to create it.' });
            return;
        }
        setSelectedProjectId(nextProjectId);
        if (!nextProjectId) {
            setLoadedProjectId('');
            return;
        }
        const loaded = await loadProject(nextProjectId);
        if (loaded) {
            void Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Project auto-loaded',
                showConfirmButton: false,
                timer: 1300,
                timerProgressBar: true,
            });
        }
    };

    const setActions = useCallback(
        (updater) => {
            setEditorData((previous) => {
                const currentActions = previous[0]?.actions || [];
                const nextActions = typeof updater === 'function' ? updater(currentActions) : updater;
                // Preserve any extra rows beyond index 0 (e.g. audio-row-main).
                const nextRows = [{ id: 'subtitle-row-main', actions: nextActions }, ...previous.slice(1)];
                latestRowsRef.current = nextRows;
                syncPayloadJson(nextRows);
                return nextRows;
            });
        },
        [syncPayloadJson],
    );

    // Delete selected subtitle action when the Delete key is pressed (but not while typing).
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== 'Delete' && event.key !== 'Backspace') return;
            const tag = (event.target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable) return;
            if (!selectedActionId) return;
            setActions((prev) => prev.filter((a) => a.id !== selectedActionId));
            setSelectedActionId('');
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedActionId, setActions]);

    useEffect(() => {
        if (!isDraggingSubtitle) return;

        const handleMouseMove = (event) => {
            const dragState = dragStateRef.current;
            if (!dragState) return;

            const precisionFactor = event.shiftKey ? 0.25 : 1;
            const deltaX = (event.clientX - dragState.startX) * precisionFactor;
            const deltaY = (event.clientY - dragState.startY) * precisionFactor;

            const previewWidthPx = Math.max(1, Number(dragState.previewWidthPx || 460));
            const boxWidthPercent = clamp(Number(dragState.initialBoxWidthPercent || 82), 20, 100);
            const boxWidthPx = (previewWidthPx * boxWidthPercent) / 100;
            const position = String(dragState.initialPosition || 'bottom-center').toLowerCase();

            const baseX = position.endsWith('left')
                ? 0
                : position.endsWith('right')
                    ? Math.max(0, previewWidthPx - boxWidthPx)
                    : Math.max(0, (previewWidthPx - boxWidthPx) / 2);

            const desiredAbsoluteX = baseX + Number(dragState.initialOffsetX || 0) + deltaX;
            const clampedAbsoluteX = clamp(desiredAbsoluteX, 0, Math.max(0, previewWidthPx - boxWidthPx));
            const nextOffsetX = Math.round(clampedAbsoluteX - baseX);
            const nextOffsetY = Math.round(dragState.initialOffsetY + deltaY);

            setActions((actions) =>
                actions.map((action) => {
                    if (action.id !== dragState.actionId) return action;
                    return {
                        ...action,
                        style: {
                            ...defaultStyle,
                            ...(action.style || {}),
                            offsetX: nextOffsetX,
                            offsetY: nextOffsetY,
                        },
                    };
                }),
            );
        };

        const handleMouseUp = () => {
            dragStateRef.current = null;
            setIsDraggingSubtitle(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingSubtitle, setActions]);

    useEffect(() => {
        if (!isResizingSubtitle) return;

        const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

        const handleMouseMove = (event) => {
            const resizeState = resizeStateRef.current;
            if (!resizeState) return;

            const precisionFactor = event.shiftKey ? 0.25 : 1;
            const deltaXRaw = (event.clientX - resizeState.startX) * precisionFactor;
            const deltaYRaw = (event.clientY - resizeState.startY) * precisionFactor;

            const horizontalSign = resizeState.handle.includes('left') ? -1 : 1;
            const verticalSign = resizeState.handle.includes('top') ? -1 : 1;

            const deltaWidthPercent = (deltaXRaw / Math.max(1, resizeState.previewWidthPx)) * 100 * horizontalSign;
            const nextWidthPercent = clamp(Math.round(resizeState.initialBoxWidthPercent + deltaWidthPercent), 20, 100);
            const nextPaddingY = clamp(
                Math.round(resizeState.initialBoxPaddingY + (deltaYRaw * verticalSign) / 3),
                0,
                40,
            );

            setActions((actions) =>
                actions.map((action) => {
                    if (action.id !== resizeState.actionId) return action;
                    return {
                        ...action,
                        style: {
                            ...defaultStyle,
                            ...(action.style || {}),
                            boxWidthPercent: nextWidthPercent,
                            boxPaddingY: nextPaddingY,
                        },
                    };
                }),
            );
        };

        const handleMouseUp = () => {
            resizeStateRef.current = null;
            setIsResizingSubtitle(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizingSubtitle, setActions]);

    useEffect(() => {
        if (!isDraggingOverlay) return;

        const handleMouseMove = (event) => {
            const dragState = overlayDragStateRef.current;
            if (!dragState) return;

            const precisionFactor = event.shiftKey ? 0.25 : 1;
            const deltaX = (event.clientX - dragState.startX) * precisionFactor;
            const deltaY = (event.clientY - dragState.startY) * precisionFactor;

            const deltaXPercent = (deltaX / Math.max(1, dragState.previewWidthPx)) * 100;
            const deltaYPercent = (deltaY / Math.max(1, dragState.previewHeightPx)) * 100;
            const nextXPercent = clamp(dragState.initialXPercent + deltaXPercent, 0, 100);
            const nextYPercent = clamp(dragState.initialYPercent + deltaYPercent, 0, 100);

            if (dragState.scope === 'global' && dragState.kind === 'shape') {
                setShapeOverlay((previous) => ({
                    ...previous,
                    xPercent: Number(nextXPercent.toFixed(2)),
                    yPercent: Number(nextYPercent.toFixed(2)),
                }));
                return;
            }

            if (dragState.scope === 'global' && dragState.kind === 'logo') {
                setLogoOverlay((previous) => ({
                    ...previous,
                    xPercent: Number(nextXPercent.toFixed(2)),
                    yPercent: Number(nextYPercent.toFixed(2)),
                }));
                return;
            }

            if (dragState.scope === 'local' && dragState.actionId) {
                setActions((actions) =>
                    actions.map((action) => {
                        if (action.id !== dragState.actionId) return action;
                        if (dragState.kind === 'shape') {
                            return {
                                ...action,
                                style: {
                                    ...defaultStyle,
                                    ...(action.style || {}),
                                    localShapeXPercent: Number(nextXPercent.toFixed(2)),
                                    localShapeYPercent: Number(nextYPercent.toFixed(2)),
                                },
                            };
                        }

                        return {
                            ...action,
                            style: {
                                ...defaultStyle,
                                ...(action.style || {}),
                                localLogoXPercent: Number(nextXPercent.toFixed(2)),
                                localLogoYPercent: Number(nextYPercent.toFixed(2)),
                            },
                        };
                    }),
                );
            }
        };

        const handleMouseUp = () => {
            overlayDragStateRef.current = null;
            setIsDraggingOverlay(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingOverlay, setActions]);

    // Track unsaved changes
    useEffect(() => {
        if (loadedProjectId) {
            setHasUnsavedChanges(true);
        }
    }, [editorData, shapeOverlay, logoOverlay, videoFadeInMs, videoFadeOutMs, reelFormat, languageName, loadedProjectId]);

    // Keep a ref to the latest saveProject so the auto-save timer always calls the current version
    const saveProjectRef = useRef(null);
    useEffect(() => {
        saveProjectRef.current = saveProject;
    });

    // Auto-save: debounce 4 seconds after last change, only for existing projects
    useEffect(() => {
        if (!hasUnsavedChanges || !loadedProjectId) return;
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(() => {
            saveProjectRef.current?.(loadedProjectId, { silent: true });
        }, 4000);
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [hasUnsavedChanges, editorData, shapeOverlay, logoOverlay, videoFadeInMs, videoFadeOutMs, reelFormat, languageName, loadedProjectId]);

    // Auto-load first project when the project list first populates. Wait until the
    // auto-create-English-projects check above has resolved, so a freshly-created
    // English/Vertical project is present in `projects` before we pick one — otherwise
    // this could auto-load a different pre-existing project first and never revisit.
    // Prefer English + vertical (the default reel format) when one exists.
    useEffect(() => {
        if (!autoCreateChecked) return;
        if (projects.length === 0 || loadedProjectId || loadingProject || hasAutoLoadedRef.current) return;
        hasAutoLoadedRef.current = true;
        const isEnglish = (p) => String(p?.languageName || '').trim().toLowerCase() === 'english';
        const preferredId = projects.find((p) => isEnglish(p) && p.reelFormat === 'vertical')?.id
            || projects.find((p) => p.reelFormat === 'vertical')?.id
            || projects[0]?.id;
        if (preferredId) {
            loadProject(preferredId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, loadedProjectId, loadingProject, autoCreateChecked]);

    const updateSelectedAction = (patch) => {
        if (!selectedActionId) return;
        setActions((actions) =>
            actions.map((action) =>
                action.id === selectedActionId
                    ? {
                          ...action,
                          ...patch,
                          style: {
                              ...defaultStyle,
                              ...(action.style || {}),
                              ...(patch.style || {}),
                          },
                      }
                    : action,
            ),
        );
    };

    const pushToSubtitleSource = useCallback(async () => {
        const actions = editorData?.[0]?.actions || [];
        if (actions.length === 0 || !selectedSource?.fileUrl) return;

        const confirmed = await Swal.fire({
            icon: 'warning',
            title: 'Push text edits to subtitle source?',
            html: `This will overwrite the <strong>${languageName}</strong> subtitle source (VTT) with the current reel text.<br/><br/>This cannot be undone. Style changes will <em>not</em> be pushed — only the text and timing.`,
            showCancelButton: true,
            confirmButtonText: 'Yes, push changes',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d97706',
        });
        if (!confirmed.isConfirmed) return;

        try {
            // Build a plain VTT from current reel actions
            const pad = (n, len = 2) => String(Math.floor(n)).padStart(len, '0');
            const toVttTime = (secs) => {
                const h = pad(secs / 3600);
                const m = pad((secs % 3600) / 60);
                const s = pad(secs % 60);
                const ms = pad(Math.round((secs % 1) * 1000), 3);
                return `${h}:${m}:${s}.${ms}`;
            };
            const vttContent =
                'WEBVTT\n\n' +
                actions
                    .map((a) => `${toVttTime(a.start)} --> ${toVttTime(a.end)}\n${(a.text || '').trim()}`)
                    .join('\n\n');

            const { uploadToBlobStorage } = await import('../../folders/subtitles/BlobVttFile');
            await uploadToBlobStorage(selectedSource.fileUrl, vttContent, 'vtt');

            Swal.fire({
                icon: 'success',
                title: 'Pushed to subtitle source',
                text: `${actions.length} subtitle(s) saved to the ${languageName} VTT.`,
                toast: true,
                position: 'top-end',
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
            });
        } catch (err) {
            console.error('Failed to push text to subtitle source:', err);
            Swal.fire({ icon: 'error', title: 'Push failed', text: 'Could not write to the subtitle source. See console for details.' });
        }
    }, [editorData, selectedSource, languageName]);

    const splitSelectedAction = useCallback(() => {
        if (!selectedAction) return;
        const words = (selectedAction.text || '').trim().split(/\s+/);
        if (words.length < 2) return;
        const midIdx = Math.ceil(words.length / 2);
        const firstText = words.slice(0, midIdx).join(' ');
        const secondText = words.slice(midIdx).join(' ');
        const midTime = (selectedAction.start + selectedAction.end) / 2;

        const firstAction = {
            ...selectedAction,
            text: firstText,
            end: midTime,
        };
        const secondAction = {
            ...selectedAction,
            id: `subtitle-split-${Date.now()}`,
            text: secondText,
            start: midTime,
            style: { ...(selectedAction.style || {}) },
        };

        setActions((actions) => {
            const idx = actions.findIndex((a) => a.id === selectedAction.id);
            if (idx === -1) return actions;
            const next = [...actions];
            next.splice(idx, 1, firstAction, secondAction);
            return next;
        });
    }, [selectedAction, setActions]);

    const applyCurrentStyleToAll = async () => {
        if (!selectedAction) return;
        const selectedStyle = { ...defaultStyle, ...(selectedAction.style || {}) };
        const sharedStyleTemplate = omitWordSpecificStyle(selectedStyle);
        const selectedWordStyle = pickWordSpecificStyle(selectedStyle);
        const shouldApplyWordStyleToAll = !preserveWordStylesOnApplyAll;

        const applyStyleAcrossActions = (actions, cueOverrides = {}) => {
            return (Array.isArray(actions) ? actions : []).map((action) => {
                const mergedExistingStyle = {
                    ...defaultStyle,
                    ...(cueOverrides?.[action.id]?.style || {}),
                    ...(action.style || {}),
                };
                return {
                    ...action,
                    style: normalizeCueStyleForPersistence({
                        ...sharedStyleTemplate,
                        ...(shouldApplyWordStyleToAll
                            ? selectedWordStyle
                            : pickWordSpecificStyle(mergedExistingStyle)),
                    }),
                };
            });
        };

        const shouldApplyToAllProjects = applyStyleScope !== 'current';

        setSharedLanguageStyle(sharedStyleTemplate);
        setActions((actions) => applyStyleAcrossActions(actions));

        if (!shouldApplyToAllProjects) {
            setMessage(null);
            void Swal.fire({
                icon: 'success',
                title: shouldApplyWordStyleToAll
                    ? 'Style and word emphasis applied'
                    : 'Style applied',
                text: shouldApplyWordStyleToAll
                    ? 'Applied to all subtitles in the current project.'
                    : 'Style applied in the current project. Word styles were preserved.',
                toast: true,
                position: 'top-end',
                timer: 2500,
                timerProgressBar: true,
                showConfirmButton: false,
            });
            return;
        }

        // Scope which projects get touched (current project always matches its own
        // language/format, so it stays included). 'language' keeps the update within
        // this language across all resolutions/formats; 'resolution' keeps it within
        // this format across all languages; 'all' spans everything for the video.
        // Fetch a fresh list rather than trusting the possibly-stale `projects` state,
        // since a project saved just before this click may not have been reflected yet.
        let latestProjects = projects;
        try {
            const fetched = await reelProjectServiceInstance.listProjects(videoId);
            if (Array.isArray(fetched)) {
                latestProjects = fetched;
                setProjects(fetched);
            }
        } catch (error) {
            console.error('Failed to refresh reel projects before applying style:', error);
        }

        const normalize = (value) => String(value || '').trim().toLowerCase();
        const allProjects = (latestProjects || []).filter((project) => {
            if (!project?.id) return false;
            if (applyStyleScope === 'language') return normalize(project.languageName) === normalize(languageName);
            if (applyStyleScope === 'resolution') return normalize(project.reelFormat) === normalize(reelFormat);
            return true; // 'all'
        });
        if (allProjects.length === 0) {
            setMessage({ severity: 'warning', text: 'No projects found in this scope to update.' });
            return;
        }

        setSaving(true);
        let failedCount = 0;
        const succeededProjects = [];
        const failedProjects = [];

        try {
            for (const projectSummary of allProjects) {
                const projectLabel = `${projectSummary.languageName || languageName} — ${platformPresets[projectSummary.reelFormat]?.name || projectSummary.reelFormat}`;
                try {
                    const project = await reelProjectServiceInstance.getProject(projectSummary.id);
                    if (!project) {
                        failedCount += 1;
                        failedProjects.push(projectLabel);
                        continue;
                    }

                    const timelineObj = JSON.parse(project.timelineDataJson || '{}');
                    const overridesObj = JSON.parse(project.subtitleOverridesJson || '{}');
                    const existingCueOverrides = overridesObj?.cueOverrides || {};

                    const projectActions = applyStyleAcrossActions(
                        extractSubtitleActions(timelineObj),
                        existingCueOverrides,
                    );

                    if (projectActions.length === 0) {
                        succeededProjects.push(projectLabel);
                        continue;
                    }

                    const subtitleTrackIndex = Array.isArray(timelineObj?.tracks)
                        ? timelineObj.tracks.findIndex((track) => track?.type === 'subtitle')
                        : -1;

                    if (subtitleTrackIndex >= 0) {
                        timelineObj.tracks[subtitleTrackIndex] = {
                            ...timelineObj.tracks[subtitleTrackIndex],
                            actions: projectActions,
                        };
                    }

                    timelineObj.editorData = [{ id: 'subtitle-row-main', actions: projectActions }];

                    const nextCueOverrides = {};
                    projectActions.forEach((action) => {
                        nextCueOverrides[action.id] = {
                            text: action.text || '',
                            style: normalizeCueStyleForPersistence(action.style),
                            start: action.start,
                            end: action.end,
                        };
                    });

                    const previousGlobalWordStyle = pickWordSpecificStyle(overridesObj?.globalStyle || {});
                    const nextOverrides = {
                        ...(overridesObj && typeof overridesObj === 'object' ? overridesObj : {}),
                        globalStyle: normalizeCueStyleForPersistence({
                            ...sharedStyleTemplate,
                            ...(shouldApplyWordStyleToAll ? selectedWordStyle : previousGlobalWordStyle),
                        }),
                        cueOverrides: nextCueOverrides,
                    };

                    const updatePayload = {
                        projectName: project.projectName || projectSummary.projectName || autoProjectName,
                        videoId: project.videoId || videoId,
                        languageName: project.languageName || languageName,
                        subtitleSourceType: project.subtitleSourceType || null,
                        subtitleSourceVersionId: project.subtitleSourceVersionId || null,
                        subtitleSourceFileUrl: project.subtitleSourceFileUrl || null,
                        subtitleSourceProcessStage: project.subtitleSourceProcessStage || null,
                        subtitleSourceHash: project.subtitleSourceHash || null,
                        subtitleApprovedBy: project.subtitleApprovedBy || null,
                        subtitleApprovedAt: project.subtitleApprovedAt || null,
                        timelineDataJson: JSON.stringify(timelineObj, null, 2),
                        subtitleOverridesJson: JSON.stringify(nextOverrides, null, 2),
                        reelFormat: project.reelFormat || reelFormat,
                    };

                    await reelProjectServiceInstance.updateProject(projectSummary.id, updatePayload);
                    succeededProjects.push(projectLabel);

                    if (projectSummary.id === (loadedProjectId || selectedProjectId)) {
                        const rows = [{ id: 'subtitle-row-main', actions: projectActions }];
                        latestRowsRef.current = rows;
                        setEditorData(rows);
                        syncPayloadJson(rows);
                    }
                } catch (error) {
                    failedCount += 1;
                    failedProjects.push(projectLabel);
                }
            }

            await loadProjects();
            const scopeLabel = applyStyleScope === 'language'
                ? `all resolutions of ${languageName}`
                : applyStyleScope === 'resolution'
                    ? `all languages of ${platformPresets[reelFormat]?.name || reelFormat}`
                    : 'all languages and resolutions for this video';
            const escapeHtml = (value) => String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            const listHtml = (items) => `<ul style="text-align:left; margin:0 auto; padding-left:1.25em; max-height:220px; overflow-y:auto;">${items
                .map((item) => `<li>${escapeHtml(item)}</li>`)
                .join('')}</ul>`;

            let html = '';
            if (succeededProjects.length > 0) {
                html += `<p style="margin:0 0 4px;"><strong>Updated (${succeededProjects.length}):</strong></p>${listHtml(succeededProjects)}`;
            }
            if (failedProjects.length > 0) {
                html += `<p style="margin:12px 0 4px;"><strong>Failed (${failedProjects.length}):</strong></p>${listHtml(failedProjects)}`;
            }

            void Swal.fire({
                icon: failedCount > 0 ? 'warning' : 'success',
                title: failedCount > 0
                    ? 'Style applied with some failures'
                    : (shouldApplyWordStyleToAll ? 'Style and word emphasis applied' : 'Style applied'),
                html: `<p style="margin:0 0 8px;">Scope: ${escapeHtml(scopeLabel)}</p>${html}`,
                confirmButtonText: 'OK',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(file);
            });
            setLogoOverlay((previous) => ({
                ...previous,
                enabled: true,
                imageUrl: dataUrl,
                // Default the visibility window to the currently selected subtitle's timing.
                ...(selectedAction && {
                    startSec: selectedAction.start,
                    endSec: selectedAction.end,
                }),
            }));
            setMessage({ severity: 'success', text: 'Logo/image overlay updated.' });
        } catch (error) {
            setMessage({ severity: 'error', text: 'Could not load image file.' });
        } finally {
            event.target.value = '';
        }
    };

    const handleLocalLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !selectedAction) return;
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Failed to read image file'));
                reader.readAsDataURL(file);
            });

            updateSelectedAction({
                style: {
                    localLogoEnabled: true,
                    localLogoImageUrl: dataUrl,
                },
            });
            setMessage({ severity: 'success', text: 'Subtitle-level logo/image updated.' });
        } catch (error) {
            setMessage({ severity: 'error', text: 'Could not load subtitle image file.' });
        } finally {
            event.target.value = '';
        }
    };

    // Keep a stable ref to editorData so timeline callbacks can read it without
    // taking it as a dependency (avoids recreating the handler on every render).
    const editorDataRef = useRef(editorData);
    useEffect(() => { editorDataRef.current = editorData; }, [editorData]);

    // Snapshot of action start/end as PRIMITIVE numbers, captured after each render.
    // Used by handleTimelineChange to detect real position changes vs zero-drag clicks.
    // We cannot compare editorDataRef directly because the library mutates action
    // objects IN-PLACE before calling onChange, so object refs always look equal.
    const actionPositionsRef = useRef({});
    useEffect(() => {
        const snapshot = {};
        (editorData?.[0]?.actions || []).forEach((a) => {
            snapshot[a.id] = { start: a.start, end: a.end };
        });
        actionPositionsRef.current = snapshot;
    }, [editorData]);

    // Stable handler passed to SubtitleTimeline so React.memo can prevent re-renders
    // when only currentTime changes (which happens every video frame ~10x/sec).
    const handleSubtitleClick = useCallback((actionId, time) => {
        selectionLockedRef.current = true;
        setSelectedActionId(actionId);
        if (!Number.isFinite(time)) {
            requestAnimationFrame(() => { requestAnimationFrame(() => { selectionLockedRef.current = false; }); });
            return;
        }
        setCurrentTime(time);
        if (videoPreviewRef.current) videoPreviewRef.current.currentTime = time;
        if (timelineRef.current?.setTime) timelineRef.current.setTime(time);
        requestAnimationFrame(() => {
            if (timelineRef.current?.setTime) timelineRef.current.setTime(time);
            requestAnimationFrame(() => { selectionLockedRef.current = false; });
        });
    }, []);

    // Stable handler — reads editorData from a ref to avoid recreating on each render.
    // Bails out while selectionLockedRef is true.
    const handleTimelineTimeChange = useCallback((time) => {
        if (!Number.isFinite(time) || selectionLockedRef.current) return;
        setCurrentTime(time);
        if (videoPreviewRef.current) videoPreviewRef.current.currentTime = time;
        const actionsAtTime = editorDataRef.current[0]?.actions || [];
        const matchingAction = actionsAtTime.find((a) => time >= a.start && time < a.end);
        if (matchingAction) setSelectedActionId(matchingAction.id);
    }, []);

    const handleTimelineChange = useCallback((nextRows) => {
        const audioRow = nextRows.find((r) => r.id === 'audio-row-main');
        if (audioRow) {
            setAudioSegments(audioRow.actions || []);
        }
        const subtitleRows = nextRows.filter((r) => r.id !== 'audio-row-main');
        latestRowsRef.current = subtitleRows;

        // The library mutates action objects IN-PLACE before calling onChange, so
        // we cannot compare object refs. Instead we compare against a snapshot of
        // PRIMITIVE start/end values captured via useEffect after the last render.
        const prevPositions = actionPositionsRef.current;
        const nextActions = subtitleRows?.[0]?.actions || [];
        const prevCount = Object.keys(prevPositions).length;
        const positionsChanged =
            nextActions.length !== prevCount ||
            nextActions.some((a) => {
                const prev = prevPositions[a.id];
                return !prev || a.start !== prev.start || a.end !== prev.end;
            });
        if (!positionsChanged) return;

        setEditorData(subtitleRows);
        syncPayloadJson(subtitleRows);
    }, [syncPayloadJson]);

    const saveProject = async (projectIdOverride = '', { silent = false } = {}) => {
        if (!videoId) {
            setMessage({ severity: 'error', text: 'Video ID is required.' });
            return null;
        }

        const targetProjectId = projectIdOverride || loadedProjectId || selectedProjectId || '';

        if (!targetProjectId) {
            const duplicateProject = (projects || []).find((project) =>
                project?.id
                && project.languageName === languageName
                && project.reelFormat === reelFormat
            );
            if (duplicateProject) {
                setMessage({
                    severity: 'warning',
                    text: `A project for ${languageName} — ${platformPresets[reelFormat]?.name || reelFormat} already exists. Load and edit it instead of creating a duplicate.`,
                });
                return null;
            }
        }

        const rowsForSave = latestRowsRef.current || editorData;
        const timelinePayload = makeTimelinePayload(rowsForSave);
        const overridesPayload = makeOverridesPayload(rowsForSave);
        const nextTimelineDataJson = JSON.stringify(timelinePayload, null, 2);
        const nextSubtitleOverridesJson = JSON.stringify(overridesPayload, null, 2);

        setTimelineDataJson(nextTimelineDataJson);
        setSubtitleOverridesJson(nextSubtitleOverridesJson);

        const payload = {
            projectName: autoProjectName,
            videoId,
            languageName,
            subtitleSourceType: selectedSource?.sourceType || null,
            subtitleSourceVersionId: selectedSource?.versionId || null,
            subtitleSourceFileUrl: selectedSource?.fileUrl || null,
            subtitleSourceProcessStage: selectedSource?.processStage || null,
            subtitleSourceHash: selectedSource?.sourceHash || null,
            subtitleApprovedBy: selectedSource?.approvedBy || null,
            subtitleApprovedAt: selectedSource?.approvedAt || null,
            timelineDataJson: nextTimelineDataJson,
            subtitleOverridesJson: nextSubtitleOverridesJson,
            reelFormat,
        };

        setSaving(true);
        try {
            const response = targetProjectId
                ? await reelProjectServiceInstance.updateProject(targetProjectId, payload)
                : await reelProjectServiceInstance.createProject(payload);
            const nextProjectId = targetProjectId || response?.id || '';

            if (nextProjectId) {
                setSelectedProjectId(nextProjectId);
                setLoadedProjectId(nextProjectId);
            }

            await loadProjects();
            setHasUnsavedChanges(false);
            setMessage(null);
            if (!silent) {
                window.Swal?.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: 'Reel project saved successfully.',
                    toast: true,
                    position: 'top-end',
                    timer: 2500,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            }
            return nextProjectId || null;
        } catch (error) {
            console.error('Failed to save reel project:', error);
            setMessage({ severity: 'error', text: 'Failed to save project.' });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteProject = async (projectIdOverride = '', projectNameOverride = '') => {
        const targetProjectId = projectIdOverride || selectedProjectId || loadedProjectId;
        if (!targetProjectId) {
            setMessage({ severity: 'warning', text: 'Select a project first to delete it.' });
            return;
        }

        const targetProject = projects.find((project) => project.id === targetProjectId);
        const targetName = projectNameOverride || targetProject?.projectName || targetProjectId;
        const confirmation = await Swal.fire({
            title: 'Delete project?',
            html: `<strong>${targetName}</strong><br/>This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Keep project',
            reverseButtons: true,
            focusCancel: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#546e7a',
        });
        if (!confirmation.isConfirmed) return;

        setLoadingProject(true);
        try {
            await reelProjectServiceInstance.deleteProject(targetProjectId);
            const deletingCurrentProject = targetProjectId === selectedProjectId || targetProjectId === loadedProjectId;
            if (deletingCurrentProject) {
                setSelectedProjectId('');
                setLoadedProjectId('');
            }
            setProjectName(autoProjectName);
            await loadProjects();
            setMessage({ severity: 'success', text: `Deleted project: ${targetName}` });
        } catch (error) {
            console.error('Failed to delete reel project:', error);
            const statusCode = Number(error?.response?.status || 0);
            if (statusCode === 404 || statusCode === 405) {
                setMessage({
                    severity: 'warning',
                    text: 'Delete is not enabled on backend yet. Please keep project and overwrite via Save for now.',
                });
            } else {
                setMessage({ severity: 'error', text: 'Failed to delete selected project.' });
            }
        } finally {
            setLoadingProject(false);
        }
    };

    const handleSaveProject = async () => {
        await saveProject(loadedProjectId || selectedProjectId || '');
    };

    const handleExportVideo = async () => {
        if (!videoId) {
            setMessage({ severity: 'error', text: 'Video ID is required before export.' });
            return;
        }

        setExporting(true);
        setExportStatus('PROCESSING');
        setDownloadUrl(null);
        try {
            // Always persist latest timeline changes before export.
            const savedProjectId = await saveProject(loadedProjectId || '');
            if (!savedProjectId) {
                setExportStatus(null);
                return;
            }

            const styleForExport = { ...defaultStyle, ...(activePreviewStyle || {}) };
            const actionsForExport = editorData?.[0]?.actions || [];

            // Scale preview-pixel values (fontSize, offsetX/Y, boxPaddingY) to export resolution.
            const previewW = Math.max(1, videoPreviewRef.current?.clientWidth || 460);
            const previewH = Math.max(1, videoPreviewRef.current?.clientHeight ||
                Math.round(previewW * selectedFormat.height / Math.max(1, selectedFormat.width)));
            const exportScaleX = selectedFormat.width / previewW;
            const exportScaleY = selectedFormat.height / previewH;

            const exportCues = actionsForExport
                .map((action) => {
                    const start = Number(action?.start);
                    const end = Number(action?.end);
                    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
                    const rawStyle = normalizeCueStyleForPersistence(action?.style);
                    return {
                        start,
                        end,
                        text: String(action?.text || ''),
                        style: {
                            ...rawStyle,
                            fontSize: Math.round(rawStyle.fontSize * exportScaleY),
                            offsetX: Math.round(rawStyle.offsetX * exportScaleX),
                            offsetY: Math.round(rawStyle.offsetY * exportScaleY),
                            boxPaddingX: Math.round((rawStyle.boxPaddingX || 14) * exportScaleX),
                            boxPaddingY: Math.round((rawStyle.boxPaddingY || 8) * exportScaleY),
                            // Scaled CSS anchor margins so backend positions to the same
                            // proportional distance from the edge as the preview (preview uses
                            // fixed preview margins in a ~640px container; export needs the scaled equivalent.
                            textShadowOffsetX: rawStyle.textShadowOffsetX || 0,
                            textShadowOffsetY: rawStyle.textShadowOffsetY || 0,
                            textShadowBlur: rawStyle.textShadowBlur || 0,
                            textShadowColor: rawStyle.textShadowColor || '#000000',
                            textShadowOpacity: rawStyle.textShadowOpacity || 0,
                            emphasisShadowBlur: rawStyle.emphasisShadowBlur ?? 8,
                            emphasisShadowOffsetY: rawStyle.emphasisShadowOffsetY ?? 2,
                            emphasisStrokeWidth: rawStyle.emphasisStrokeWidth, // This is a percentage (0-15), backend handles scaling
                            _anchorY: Math.round(SUBTITLE_ANCHOR_MARGIN_Y * exportScaleY),
                            _anchorX: Math.round(SUBTITLE_ANCHOR_MARGIN_X * exportScaleX),
                        },
                    };
                })
                .filter(Boolean);

            const subtitleContent = buildSrtFromActions(actionsForExport);
            if (!subtitleContent) {
                setMessage({ severity: 'warning', text: 'No subtitle cues available to export.' });
                return;
            }

            // Only include shape/logo overlays if explicitly enabled
            const globalShapeForExport = shapeOverlay?.enabled ? { ...shapeOverlay } : {};
            const globalImageForExport = logoOverlay?.enabled ? { ...logoOverlay } : {};

            const exportPayload = {
                videoId,
                language: languageName,
                subtitleFormat: 'srt',
                subtitleContent,
                renderManifest: {
                    cues: exportCues,
                    globalShape: globalShapeForExport,
                    globalImage: globalImageForExport,
                    videoFadeInMs,
                    videoFadeOutMs,
                    muteVideoAudio,
                    ...(dubbedAudioUrl && audioSegments.length > 0
                        ? { audioTrack: { src: dubbedAudioUrl, segments: audioSegments } }
                        : {}),
                },
                format: {
                    name: selectedFormat.name,
                    aspectRatio: selectedFormat.ratio,
                    resolution: `${selectedFormat.width}x${selectedFormat.height}`,
                },
                subtitleStyle: {
                    fontFamily: styleForExport.fontFamily,
                    fontSize: Number(styleForExport.fontSize || 44),
                    fontColor: styleForExport.color || '#ffffff',
                    bold: Boolean(styleForExport.bold),
                    italic: Boolean(styleForExport.italic),
                    underline: Boolean(styleForExport.underline),
                    backgroundColor: styleForExport.backgroundColor || '#000000',
                    backgroundOpacity: Math.max(0, Math.min(1, Number(styleForExport.backgroundOpacity || 0) / 100)),
                    position: styleForExport.position || 'bottom-center',
                    boxPadding: Number(styleForExport.boxPaddingY || 8),
                    boxMargin: Math.max(0, Math.abs(Number(styleForExport.offsetY || 0))),
                },
            };

            const exportResponse = await reelProjectServiceInstance.exportVideo(exportPayload);
            const initialStatus = exportResponse?.status || 'PROCESSING';
            const initialDownloadUrl = exportResponse?.downloadUrl || exportResponse?.azureBlobUrl || '';
            const jobId = exportResponse?.jobId || '';

            if (initialDownloadUrl) {
                window.open(initialDownloadUrl, '_blank');
                setExportStatus('COMPLETED');
                setDownloadUrl(initialDownloadUrl);
                setMessage(null);
                void Swal.fire({
                    icon: 'success',
                    title: 'Export Video',
                    text: 'Export complete. Video opened in a new tab.',
                    toast: true,
                    position: 'top-end',
                    timer: 5000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
                return;
            }

            setMessage(null);
            void Swal.fire({
                icon: 'info',
                title: 'Export Started',
                text: `Export ${initialStatus}. We will open download when ready.`,
                toast: true,
                position: 'top-end',
                timer: 2500,
                timerProgressBar: true,
                showConfirmButton: false,
            });

            if (!jobId) {
                setExportStatus(null);
                setDownloadUrl(null);
                setMessage({
                    severity: 'error',
                    text: 'Export request did not return a job ID. Please try again.',
                });
                return;
            }

            const maxPollCount = 720; // ~60 minutes at 5s interval — large videos can take a while to render
            for (let pollCount = 0; pollCount < maxPollCount; pollCount += 1) {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                const statusResponse = await reelProjectServiceInstance.getExportStatus(jobId);
                const status = statusResponse?.status || 'PROCESSING';
                const readyUrl = statusResponse?.downloadUrl || statusResponse?.azureBlobUrl || '';

                if (status === 'COMPLETED') {
                    setExportStatus('COMPLETED');
                    setDownloadUrl(readyUrl);
                    if (readyUrl) {
                        window.open(readyUrl, '_blank');
                    }
                    setMessage(null);
                    void Swal.fire({
                        icon: 'success',
                        title: 'Export Complete',
                        text: 'Video export completed successfully.',
                        toast: true,
                        position: 'top-end',
                        timer: 3500,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    });
                    return;
                }

                if (status === 'FAILED') {
                    const failMessage = statusResponse?.message || 'Video export failed.';
                    setExportStatus(null);
                    setDownloadUrl(null);
                    setMessage({ severity: 'error', text: failMessage });
                    return;
                }
            }

            setExportStatus(null);
            setDownloadUrl(null);
            setMessage({
                severity: 'warning',
                text: 'Export is still processing. Please check back in a few minutes.',
            });
        } catch (error) {
            console.error('Failed to export reel project:', error);
            const backendMessage = error?.response?.data?.message;
            setExportStatus(null);
            setDownloadUrl(null);
            setMessage({ severity: 'error', text: backendMessage || 'Failed to start video export.' });
        } finally {
            setExporting(false);
        }
    };

    const selectedFormat = platformPresets[reelFormat] || platformPresets.vertical;
    const isNewProjectContext = !selectedProjectId && !loadedProjectId;

    const activePreviewAction = useMemo(() => {
        const actions = editorData?.[0]?.actions || [];
        // Use < action.end (exclusive) so the next subtitle correctly takes over at shared boundary timestamps.
        // Return null when currentTime is in a gap — do NOT fall back to selectedAction, which would
        // incorrectly show the next (or any selected) subtitle during periods with no subtitle.
        return actions.find((action) => currentTime >= action.start && currentTime < action.end) || null;
    }, [editorData, currentTime]);

    const activePreviewStyle = {
        ...defaultStyle,
        ...(activePreviewAction?.style || {}),
    };

    const activePreviewAnimation = useMemo(
        () => getAnimationPreviewStyle(activePreviewAction, currentTime),
        [activePreviewAction, currentTime],
    );

    const subtitleOverlayPositionStyle = getOverlayPositionStyle(activePreviewStyle.position);
    const overlayTransform = `${subtitleOverlayPositionStyle.baseTransform || ''} translate(${Number(
        activePreviewStyle.offsetX || 0,
    )}px, ${Number(activePreviewStyle.offsetY || 0)}px)`
        .trim()
        .replace(/^translate\(0px, 0px\)$/g, '');

    const startSubtitleDrag = (event) => {
        if (!activePreviewAction?.id) return;

        event.preventDefault();
        event.stopPropagation();

        dragStateRef.current = {
            actionId: activePreviewAction.id,
            startX: event.clientX,
            startY: event.clientY,
            initialOffsetX: Number(activePreviewStyle.offsetX || 0),
            initialOffsetY: Number(activePreviewStyle.offsetY || 0),
            previewWidthPx: Number(videoPreviewRef.current?.clientWidth || 460),
            initialBoxWidthPercent: Number(activePreviewStyle.boxWidthPercent || 82),
            initialPosition: String(activePreviewStyle.position || 'bottom-center'),
        };

        setSelectedActionId(activePreviewAction.id);
        setIsDraggingSubtitle(true);
    };

    const startSubtitleResize = (event, handle) => {
        if (!activePreviewAction?.id) return;

        event.preventDefault();
        event.stopPropagation();

        resizeStateRef.current = {
            actionId: activePreviewAction.id,
            handle,
            startX: event.clientX,
            startY: event.clientY,
            previewWidthPx: Number(videoPreviewRef.current?.clientWidth || 460),
            initialBoxWidthPercent: Number(activePreviewStyle.boxWidthPercent || 82),
            initialBoxPaddingY: Number(activePreviewStyle.boxPaddingY || 8),
        };

        setSelectedActionId(activePreviewAction.id);
        setIsResizingSubtitle(true);
    };

    const startOverlayDrag = (event, options) => {
        const { scope, kind, actionId = '' } = options || {};
        if (!scope || !kind) return;

        const previewEl = videoPreviewRef.current;
        if (!previewEl) return;

        let initialXPercent = 0;
        let initialYPercent = 0;

        if (scope === 'global' && kind === 'shape') {
            initialXPercent = Number(shapeOverlay.xPercent || 0);
            initialYPercent = Number(shapeOverlay.yPercent || 0);
        } else if (scope === 'global' && kind === 'logo') {
            initialXPercent = Number(logoOverlay.xPercent || 0);
            initialYPercent = Number(logoOverlay.yPercent || 0);
        } else if (scope === 'local' && kind === 'shape') {
            initialXPercent = Number(activePreviewStyle.localShapeXPercent || 0);
            initialYPercent = Number(activePreviewStyle.localShapeYPercent || 0);
        } else if (scope === 'local' && kind === 'logo') {
            initialXPercent = Number(activePreviewStyle.localLogoXPercent || 0);
            initialYPercent = Number(activePreviewStyle.localLogoYPercent || 0);
        } else {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        overlayDragStateRef.current = {
            scope,
            kind,
            actionId: scope === 'local' ? actionId : '',
            startX: event.clientX,
            startY: event.clientY,
            previewWidthPx: Number(previewEl.clientWidth || 460),
            previewHeightPx: Number(previewEl.clientHeight || 780),
            initialXPercent,
            initialYPercent,
        };

        if (scope === 'local' && actionId) {
            setSelectedActionId(actionId);
        }
        setIsDraggingOverlay(true);
    };

    // Increase the allowed preview background opacity range so that both light and dark text remain readable
    // against a more solid backdrop in the editor interface.
    const previewBgOpacity = clamp(Number(activePreviewStyle.backgroundOpacity ?? defaultStyle.backgroundOpacity), 10, 85);
    const previewBgStyle = { ...(activePreviewStyle || {}), backgroundOpacity: previewBgOpacity };

    // Calculate a dynamic shadow based on text brightness to ensure legibility
    const textRgb = hexToRgb(activePreviewStyle.color || '#ffffff') || { r: 255, g: 255, b: 255 };
    const textBrightness = (0.299 * textRgb.r + 0.587 * textRgb.g + 0.114 * textRgb.b);
    const dynamicShadow = textBrightness < 120 ? '0 0 3px rgba(255,255,255,0.7)' : '0 1px 3px rgba(0,0,0,0.8)';

    const subtitlePreviewStyle = {
        fontFamily: activePreviewStyle.fontFamily || defaultStyle.fontFamily,
        fontSize: `${activePreviewStyle.fontSize || defaultStyle.fontSize}px`,
        color: activePreviewStyle.color || defaultStyle.color,
        background: resolveSubtitleBackground(previewBgStyle || defaultStyle),
        textAlign: activePreviewStyle.align || defaultStyle.align,
        fontWeight: activePreviewStyle.bold ? 700 : 400,
        fontStyle: activePreviewStyle.italic ? 'italic' : 'normal',
        textDecoration: activePreviewStyle.underline ? 'underline' : 'none',
        lineHeight: activePreviewStyle.lineHeight || defaultStyle.lineHeight,
        letterSpacing: `${activePreviewStyle.letterSpacing || defaultStyle.letterSpacing}px`,
        borderRadius: '10px',
        display: 'inline-block',
        padding: '8px 14px',
        maxWidth: '100%',
        textShadow: activePreviewStyle.textShadowBlur > 0 ? `${activePreviewStyle.textShadowOffsetX || 0}px ${activePreviewStyle.textShadowOffsetY || 0}px ${activePreviewStyle.textShadowBlur}px ${activePreviewStyle.textShadowColor || '#000000'}` : 'none',
        boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
        border: '1px solid rgba(255,255,255,0.06)'
    };

    const shapeBorderRadius =
        shapeOverlay.type === 'circle' ? '50%' : `${Math.max(0, Number(shapeOverlay.radiusPx ?? defaultShapeOverlay.radiusPx))}px`;
    const localShapeBorderRadius =
        (activePreviewStyle.localShapeType || 'rounded-box') === 'circle'
            ? '50%'
            : `${Math.max(0, Number(activePreviewStyle.localShapeRadiusPx ?? 12))}px`;

    const previewFrameMaxWidth = selectedFormat.ratio === '9:16' ? 360 : 520;
    const reelParentPath = useMemo(() => {
        const fromState = location.state?.folderPath;
        if (typeof fromState === 'string' && fromState.trim().length > 0) {
            const normalized = fromState.startsWith('/') ? fromState : `/${fromState}`;
            return normalized.replace(/\/+$/g, '') || '/';
        }

        try {
            const raw = localStorage.getItem('breadcrumbs');
            const parsed = JSON.parse(raw || '[]');
            if (Array.isArray(parsed) && parsed.length > 0) {
                return `/${parsed.join('/')}`;
            }
        } catch (error) {
            // Ignore breadcrumb cache parse failures and fallback to root.
        }

        return '/';
    }, [location.state]);

    const handleBreadcrumbNavigate = useCallback(
        (crumb) => {
            const targetPath = crumb?.path || '/';
            navigate('/', {
                state: {
                    fromVideoEdit: true,
                    folderPath: targetPath,
                    refreshNeeded: false,
                },
            });
        },
        [navigate],
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: darkMode ? '#0f172a' : '#f8fafc' }}>
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />

            <Box sx={{ p: 2 }}>
                <Paper variant="outlined" sx={{ px: 1.25, py: 0.75, mb: 1.25, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <FolderBreadcrumbs currentPath={reelParentPath} onNavigate={handleBreadcrumbNavigate} />
                    </Box>
                </Paper>

                {message && (
                    <Alert
                        severity={message.severity || 'info'}
                        onClose={() => setMessage(null)}
                        sx={{ mb: 2 }}
                    >
                        {message.text}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* ── Toolbar bar — full width ── */}
                    <Grid item xs={12}>
                        <Paper id="reel-editor-platform" sx={{ p: 2, borderRadius: 3 }}>
                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: 8,
                                    zIndex: 2,
                                    mb: 1.5,
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: darkMode ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,0.92)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backdropFilter: 'blur(6px)',
                                }}
                            >
                                <Stack spacing={1.2}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                        <TextField
                                            label="Project Name"
                                            size="small"
                                            value={projectName}
                                            InputProps={{ readOnly: true }}
                                            sx={{ flex: '1 1 320px', minWidth: { xs: '100%', md: 320 } }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
                                            <InputLabel id="language-label">Language</InputLabel>
                                            <Select
                                                labelId="language-label"
                                                value={languageName}
                                                label="Language"
                                                onChange={(event) => setLanguageName(event.target.value)}
                                                disabled={!isNewProjectContext}
                                            >
                                                {availableLanguages.map((language) => (
                                                    <MenuItem key={language} value={language}>
                                                        {language}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 210 } }}>
                                            <InputLabel id="reel-format-label">Resolution</InputLabel>
                                            <Select
                                                labelId="reel-format-label"
                                                value={reelFormat}
                                                label="Resolution"
                                                onChange={(event) => setReelFormat(event.target.value)}
                                                disabled={!isNewProjectContext}
                                            >
                                                {Object.entries(platformPresets).map(([value, config]) => (
                                                    <MenuItem key={value} value={value}>
                                                        {config.name} ({config.ratio})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 300 } }}>
                                            <InputLabel id="project-shortcut-label">Project</InputLabel>
                                            <Select
                                                labelId="project-shortcut-label"
                                                label="Project"
                                                value={selectedProjectId || '__new__'}
                                                onChange={handleProjectSelectionChange}
                                                disabled={loadingProject}
                                            >
                                                <MenuItem value="__new__">+ New Project</MenuItem>
                                                {projects.map((project) => (
                                                    <MenuItem key={project.id} value={project.id}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1.5 }}>
                                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {project.projectName || project.id}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onMouseDown={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                }}
                                                                onClick={async (event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    await handleDeleteProject(project.id, project.projectName || project.id);
                                                                }}
                                                                aria-label={`Delete ${project.projectName || project.id}`}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Button
                                            variant="contained"
                                            onClick={handleSaveProject}
                                            disabled={saving}
                                            startIcon={saving ? null : <SaveIcon />}
                                            sx={{ minWidth: 110, position: 'relative' }}
                                        >
                                            {saving ? <CircularProgress size={18} /> : 'Save'}
                                            {hasUnsavedChanges && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -6,
                                                        right: -6,
                                                        width: 14,
                                                        height: 14,
                                                        backgroundColor: '#ef4444',
                                                        borderRadius: '50%',
                                                        border: '2px solid #fff',
                                                    }}
                                                />
                                            )}
                                        </Button>
                                        <Button
                                            id="reel-editor-export"
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleExportVideo}
                                            disabled={saving || exporting}
                                            sx={{ minWidth: 130 }}
                                        >
                                            {exporting ? <CircularProgress size={18} /> : 'Export Video'}
                                        </Button>
                                        <Button
                                            id="reel-editor-bulk-export"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => setBulkExportOpen(true)}
                                            disabled={projects.length === 0}
                                            sx={{ minWidth: 130 }}
                                        >
                                            Bulk Export
                                        </Button>
                                        {exportStatus === 'PROCESSING' && (
                                            <Alert severity="info" sx={{ py: 0.3 }}>
                                                Exporting in progress. Please wait...
                                            </Alert>
                                        )}
                                        {exportStatus === 'COMPLETED' && downloadUrl && (
                                            <Alert
                                                severity="success"
                                                action={
                                                    <Button
                                                        color="inherit"
                                                        size="small"
                                                        onClick={() => window.open(downloadUrl, '_blank')}
                                                    >
                                                        Download
                                                    </Button>
                                                }
                                                sx={{ py: 0.3 }}
                                            >
                                                Export complete. Your video is ready.
                                            </Alert>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { md: 'auto' } }}>
                                            <Tooltip title="Advanced controls">
                                                <IconButton
                                                    onClick={() => setShowAdvancedControls((previous) => !previous)}
                                                    aria-label="Toggle advanced controls"
                                                >
                                                    <SettingsOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Help">
                                                <IconButton
                                                    onClick={() => setShowHelpPanel((previous) => !previous)}
                                                    aria-label="Toggle help"
                                                >
                                                    <HelpOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Box>

                            <Collapse in={showHelpPanel}>
                                <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, mb: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.7 }}>
                                        Editor Help
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.4 }}>
                                        Preview time is the current video playhead time shown in seconds.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.4 }}>
                                        Preview ratio is the output format ratio (for example 9:16 for reels).
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.4 }}>
                                        Drag subtitles directly on the preview; hold Shift for finer movement and resize with the corner handles.
                                    </Typography>
                                </Paper>
                            </Collapse>

                            <Collapse in={showAdvancedControls}>
                                <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, mb: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Advanced Controls
                                    </Typography>

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems="center">
                                        <TextField
                                            label="Video ID (metadata)"
                                            value={videoId}
                                            onChange={(event) => setVideoId(event.target.value)}
                                            size="small"
                                            sx={{ minWidth: { xs: '100%', md: 280 } }}
                                        />
                                        
                                        <Button variant="outlined" size="small" onClick={loadVideoData}>
                                            Get File Details
                                        </Button>
                                        <Button variant="outlined" size="small" onClick={loadProjects} disabled={loadingProjects}>
                                            {loadingProjects ? <CircularProgress size={18} /> : 'Refresh Project List'}
                                        </Button>
                                        <Chip label={`${selectedFormat.width} x ${selectedFormat.height}`} color="info" />
                                    </Stack>

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel id="subtitle-source-label">Subtitle Source</InputLabel>
                                            <Select
                                                labelId="subtitle-source-label"
                                                value={selectedSourceHash}
                                                label="Subtitle Source"
                                                onChange={(event) => setSelectedSourceHash(event.target.value)}
                                            >
                                                {sources.map((source) => (
                                                    <MenuItem key={source.sourceHash} value={source.sourceHash}>
                                                        {source.processStage || 'N/A'} | v{source.versionNumber || '-'} | {source.sourceType || 'source'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button variant="contained" size="small" onClick={hydrateFromSource}>
                                            Import Source
                                        </Button>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                        <Button variant="text" size="small" onClick={loadSources} disabled={loadingSources}>
                                            {loadingSources ? <CircularProgress size={16} /> : 'Reload Sources'}
                                        </Button>
                                    </Stack>

                                    {(videoUrl || videoLoadError) && (
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1 }}>
                                            {videoUrl && (
                                                <Chip
                                                    color="primary"
                                                    variant="outlined"
                                                    label={`Detected Video URL: ${videoUrl.length > 96 ? `${videoUrl.slice(0, 96)}...` : videoUrl}`}
                                                />
                                            )}
                                            {videoUrl && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    onClick={() => window.open(videoUrl, '_blank', 'noopener,noreferrer')}
                                                >
                                                    Open Video URL
                                                </Button>
                                            )}
                                            {videoLoadError && <Alert severity="warning">{videoLoadError}</Alert>}
                                        </Stack>
                                    )}
                                </Paper>
                            </Collapse>

                        </Paper>
                    </Grid>

                    {/* ── LEFT column — video + subtitle blocks ── */}
                    <Grid item xs={12} md={4} sx={{ position: { md: 'sticky' }, top: { md: 16 }, alignSelf: { md: 'flex-start' } }}>
                        {/* Video panel */}
                        <Paper id="reel-editor-canvas" sx={{ p: 1.5, borderRadius: 3, mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    bgcolor: '#000',
                                    minHeight: { xs: 300, md: 460 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                        {videoUrl ? (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: previewFrameMaxWidth,
                                                    mx: 'auto',
                                                    aspectRatio: `${selectedFormat.width} / ${selectedFormat.height}`,
                                                    position: 'relative',
                                                    bgcolor: '#000',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    border: '1px solid rgba(255,255,255,0.12)',
                                                }}
                                            >
                                        <video
                                            ref={videoPreviewRef}
                                            key={`${videoUrl}-${selectedFormat.width}-${selectedFormat.height}`}
                                            controls
                                            playsInline
                                            preload="metadata"
                                            crossOrigin="anonymous"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                            src={videoUrl}
                                            onTimeUpdate={(event) => {
                                                const nextTime = Number(event.currentTarget.currentTime || 0);
                                                setCurrentTime(nextTime);
                                            }}
                                            onError={() => {
                                                setVideoLoadError('Video URL was detected but playback failed in this browser context.');
                                            }}
                                            onLoadedData={() => {
                                                setVideoLoadError('');
                                            }}
                                        />

                                        {/* Hidden audio element for dubbed audio preview sync */}
                                        <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />

                                        {shapeOverlay.enabled && (
                                            <Box
                                                onMouseDown={(event) => startOverlayDrag(event, { scope: 'global', kind: 'shape' })}
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${clamp(Number(shapeOverlay.xPercent || 0), 0, 100)}%`,
                                                    top: `${clamp(Number(shapeOverlay.yPercent || 0), 0, 100)}%`,
                                                    width: `${clamp(Number(shapeOverlay.widthPercent || 10), 1, 100)}%`,
                                                    height: `${clamp(Number(shapeOverlay.heightPercent || 10), 1, 100)}%`,
                                                    background: resolveColorWithOpacity(
                                                        shapeOverlay.fillColor,
                                                        shapeOverlay.fillOpacity,
                                                        '#2563eb',
                                                    ),
                                                    border: `${Math.max(0, Number(shapeOverlay.borderWidth || 0))}px solid ${resolveColorWithOpacity(
                                                        shapeOverlay.borderColor,
                                                        shapeOverlay.borderOpacity,
                                                        '#ffffff',
                                                    )}`,
                                                    borderRadius: shapeBorderRadius,
                                                    transform: `rotate(${Number(shapeOverlay.rotationDeg || 0)}deg)`,
                                                    transformOrigin: 'center center',
                                                    pointerEvents: 'auto',
                                                    cursor: isDraggingOverlay ? 'grabbing' : 'grab',
                                                }}
                                            />
                                        )}

                                        {logoOverlay.enabled && logoOverlay.imageUrl && (
                                            (logoOverlay.startSec === null || logoOverlay.startSec === undefined || currentTime >= logoOverlay.startSec) &&
                                            (logoOverlay.endSec === null || logoOverlay.endSec === undefined || currentTime < logoOverlay.endSec)
                                        ) && (
                                            <Box
                                                component="img"
                                                onMouseDown={(event) => startOverlayDrag(event, { scope: 'global', kind: 'logo' })}
                                                alt="overlay-logo"
                                                src={logoOverlay.imageUrl}
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${clamp(Number(logoOverlay.xPercent || 0), 0, 100)}%`,
                                                    top: `${clamp(Number(logoOverlay.yPercent || 0), 0, 100)}%`,
                                                    width: `${clamp(Number(logoOverlay.widthPercent || 5), 1, 100)}%`,
                                                    height: `${clamp(Number(logoOverlay.heightPercent || 5), 1, 100)}%`,
                                                    objectFit: logoOverlay.fit || 'contain',
                                                    opacity: clamp(Number(logoOverlay.opacity || 100), 0, 100) / 100,
                                                    pointerEvents: 'auto',
                                                    cursor: isDraggingOverlay ? 'grabbing' : 'grab',
                                                }}
                                            />
                                        )}

                                        {Boolean(activePreviewStyle.localShapeEnabled) && (
                                            <Box
                                                onMouseDown={(event) =>
                                                    startOverlayDrag(event, {
                                                        scope: 'local',
                                                        kind: 'shape',
                                                        actionId: activePreviewAction?.id || '',
                                                    })
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${clamp(Number(activePreviewStyle.localShapeXPercent || 0), 0, 100)}%`,
                                                    top: `${clamp(Number(activePreviewStyle.localShapeYPercent || 0), 0, 100)}%`,
                                                    width: `${clamp(Number(activePreviewStyle.localShapeWidthPercent || 1), 1, 100)}%`,
                                                    height: `${clamp(Number(activePreviewStyle.localShapeHeightPercent || 1), 1, 100)}%`,
                                                    background: resolveColorWithOpacity(
                                                        activePreviewStyle.localShapeFillColor,
                                                        activePreviewStyle.localShapeFillOpacity,
                                                        '#f59e0b',
                                                    ),
                                                    border: `${Math.max(0, Number(activePreviewStyle.localShapeBorderWidth || 0))}px solid ${resolveColorWithOpacity(
                                                        activePreviewStyle.localShapeBorderColor,
                                                        activePreviewStyle.localShapeBorderOpacity,
                                                        '#ffffff',
                                                    )}`,
                                                    borderRadius: localShapeBorderRadius,
                                                    transform: `rotate(${Number(activePreviewStyle.localShapeRotationDeg || 0)}deg)`,
                                                    transformOrigin: 'center center',
                                                    pointerEvents: 'auto',
                                                    cursor: isDraggingOverlay ? 'grabbing' : 'grab',
                                                }}
                                            />
                                        )}

                                        {Boolean(activePreviewStyle.localLogoEnabled) && activePreviewStyle.localLogoImageUrl && (
                                            <Box
                                                component="img"
                                                onMouseDown={(event) =>
                                                    startOverlayDrag(event, {
                                                        scope: 'local',
                                                        kind: 'logo',
                                                        actionId: activePreviewAction?.id || '',
                                                    })
                                                }
                                                alt="subtitle-overlay-image"
                                                src={activePreviewStyle.localLogoImageUrl}
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${clamp(Number(activePreviewStyle.localLogoXPercent || 0), 0, 100)}%`,
                                                    top: `${clamp(Number(activePreviewStyle.localLogoYPercent || 0), 0, 100)}%`,
                                                    width: `${clamp(Number(activePreviewStyle.localLogoWidthPercent || 1), 1, 100)}%`,
                                                    height: `${clamp(Number(activePreviewStyle.localLogoHeightPercent || 1), 1, 100)}%`,
                                                    objectFit: activePreviewStyle.localLogoFit || 'contain',
                                                    opacity: clamp(Number(activePreviewStyle.localLogoOpacity || 100), 0, 100) / 100,
                                                    pointerEvents: 'auto',
                                                    cursor: isDraggingOverlay ? 'grabbing' : 'grab',
                                                }}
                                            />
                                        )}

                                        {activePreviewAction?.text?.trim() && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    pointerEvents: 'none',
                                                    width: `${Math.max(20, Math.min(100, Number(activePreviewStyle.boxWidthPercent || 82)))}%`,
                                                    top: subtitleOverlayPositionStyle.top,
                                                    left: subtitleOverlayPositionStyle.left,
                                                    right: subtitleOverlayPositionStyle.right,
                                                    bottom: subtitleOverlayPositionStyle.bottom,
                                                    transform: overlayTransform || undefined,
                                                }}
                                            >
                                                <Box
                                                    onMouseDown={startSubtitleDrag}
                                                    sx={{
                                                        maxWidth: '100%',
                                                        width: '100%',
                                                        // Per-word spans (see renderTextWithWordEmphasis) now carry the entrance/exit
                                                        // animation, staggered word-by-word; the box itself stays fully visible so the
                                                        // two don't compound into a double fade/pop.
                                                        opacity: 1,
                                                        transform: undefined,
                                                        transformOrigin: 'center bottom',
                                                        px: Number(activePreviewStyle.boxPaddingX || 14) / 8,
                                                        py: Number(activePreviewStyle.boxPaddingY || 8) / 8,
                                                        borderRadius: `${Math.max(0, Number(activePreviewStyle.boxRadius || 10))}px`,
                                                        bgcolor: resolveSubtitleBackground(activePreviewStyle),
                                                        color: activePreviewStyle.color || '#fff',
                                                        fontFamily: activePreviewStyle.fontFamily || defaultStyle.fontFamily,
                                                        fontWeight: activePreviewStyle.bold ? 700 : 400,
                                                        fontStyle: activePreviewStyle.italic ? 'italic' : 'normal',
                                                        textDecoration: activePreviewStyle.underline ? 'underline' : 'none',
                                                        fontSize: `${Math.max(10, Number(activePreviewStyle.fontSize || 32))}px`,
                                                        lineHeight: activePreviewStyle.lineHeight || 1.2,
                                                        letterSpacing: `${activePreviewStyle.letterSpacing || 0}px`,
                                                        textAlign: activePreviewStyle.align || 'center',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        textShadow: activePreviewStyle.textShadowBlur > 0 ? `${activePreviewStyle.textShadowOffsetX || 0}px ${activePreviewStyle.textShadowOffsetY || 0}px ${activePreviewStyle.textShadowBlur}px ${activePreviewStyle.textShadowColor || '#000000'}` : 'none',
                                                        pointerEvents: 'auto',
                                                        userSelect: 'none',
                                                        cursor: isDraggingSubtitle ? 'grabbing' : 'grab',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {renderTextWithWordEmphasis(activePreviewAction.text, activePreviewStyle, {
                                                        action: activePreviewAction,
                                                        currentTime,
                                                    })}

                                                    <Box
                                                        onMouseDown={(event) => startSubtitleResize(event, 'top-left')}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -5,
                                                            left: -5,
                                                            width: 10,
                                                            height: 10,
                                                            bgcolor: '#fff',
                                                            border: '1px solid #111827',
                                                            borderRadius: '2px',
                                                            cursor: 'nwse-resize',
                                                            pointerEvents: 'auto',
                                                        }}
                                                    />
                                                    <Box
                                                        onMouseDown={(event) => startSubtitleResize(event, 'top-right')}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -5,
                                                            right: -5,
                                                            width: 10,
                                                            height: 10,
                                                            bgcolor: '#fff',
                                                            border: '1px solid #111827',
                                                            borderRadius: '2px',
                                                            cursor: 'nesw-resize',
                                                            pointerEvents: 'auto',
                                                        }}
                                                    />
                                                    <Box
                                                        onMouseDown={(event) => startSubtitleResize(event, 'bottom-left')}
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -5,
                                                            left: -5,
                                                            width: 10,
                                                            height: 10,
                                                            bgcolor: '#fff',
                                                            border: '1px solid #111827',
                                                            borderRadius: '2px',
                                                            cursor: 'nesw-resize',
                                                            pointerEvents: 'auto',
                                                        }}
                                                    />
                                                    <Box
                                                        onMouseDown={(event) => startSubtitleResize(event, 'bottom-right')}
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -5,
                                                            right: -5,
                                                            width: 10,
                                                            height: 10,
                                                            bgcolor: '#fff',
                                                            border: '1px solid #111827',
                                                            borderRadius: '2px',
                                                            cursor: 'nwse-resize',
                                                            pointerEvents: 'auto',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ p: 2 }}>
                                                <Alert severity="info">Video URL is unavailable for this video.</Alert>
                                            </Box>
                                        )}
                                    </Box>
                        </Paper>

                        {/* Subtitle blocks list */}
                        <Paper sx={{ p: 1.25, borderRadius: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Subtitle Blocks
                            </Typography>
                            <Stack ref={subtitleListRef} spacing={1} sx={{ maxHeight: { xs: 220, md: 400 }, overflowY: 'auto' }}>
                                {(editorData[0]?.actions || []).map((action) => {
                                    const isActive = selectedActionId === action.id;
                                    return (
                                        <Box
                                            key={action.id}
                                            data-action-id={action.id}
                                            sx={{
                                                p: 1,
                                                borderRadius: 1.5,
                                                border: isActive ? '2px solid #2563eb' : '1px solid #d1d5db',
                                                bgcolor: isActive ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => seekToSubtitleAction(action)}
                                        >
                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                {action.start.toFixed(2)}s - {action.end.toFixed(2)}s
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {buildActionLabel(action)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* ── RIGHT column — timeline + subtitle preview + editing controls ── */}
                    <Grid item xs={12} md={8}>
                        {/* Timeline + subtitle preview */}
                        <Paper sx={{ p: 1.5, borderRadius: 3, mb: 1.5 }}>
                            {/* Live subtitle preview */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    p: 1.25,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                        <Typography variant="subtitle2">Live Subtitle Preview @ {currentTime.toFixed(2)}s</Typography>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                background: 'linear-gradient(180deg, #94a3b8 0%, #64748b 35%, #334155 100%)',
                                                borderRadius: 2,
                                                minHeight: 130,
                                                border: '1px solid rgba(255,255,255,0.10)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box sx={subtitlePreviewStyle}>
                                                {renderTextWithWordEmphasis(activePreviewAction?.text || '', activePreviewStyle || defaultStyle, activePreviewAction ? {
                                                    action: activePreviewAction,
                                                    currentTime,
                                                } : undefined)}
                                            </Box>
                                        </Box>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    size="small"
                                                    checked={preserveWordStylesOnApplyAll}
                                                    onChange={(event) => setPreserveWordStylesOnApplyAll(event.target.checked)}
                                                />
                                            )}
                                            label="Preserve Word styles"
                                        />
                                        <FormControl size="small" sx={{ mt: 0.5 }}>
                                            <FormLabel sx={{ fontSize: '0.8rem' }}>Apply style to</FormLabel>
                                            <RadioGroup
                                                value={applyStyleScope}
                                                onChange={(event) => setApplyStyleScope(event.target.value)}
                                            >
                                                <FormControlLabel value="current" control={<Radio size="small" />} label="Just this project (current language + format)" />
                                                <FormControlLabel value="language" control={<Radio size="small" />} label={`All resolutions of ${languageName} (Horizontal, Vertical, Square)`} />
                                                <FormControlLabel value="resolution" control={<Radio size="small" />} label={`All languages of ${platformPresets[reelFormat]?.name || reelFormat}`} />
                                                <FormControlLabel value="all" control={<Radio size="small" />} label="Every language and format for this video" />
                                            </RadioGroup>
                                        </FormControl>
                                        <Button variant="contained" onClick={applyCurrentStyleToAll} disabled={!selectedAction}>
                                            {applyStyleScope === 'current' ? 'Apply Style To This Project' : 'Apply Style To Selected Scope'}
                                        </Button>
                                        <Typography variant="caption" color="text.secondary">
                                            Tip: drag subtitle text, shape, and image directly on the video preview.
                                        </Typography>
                            </Paper>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Preview {currentTime.toFixed(2)}s | {selectedFormat.ratio}
                            </Typography>

                            <Box id="reel-editor-subtitles" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.8 }}>
                                    Timeline
                                </Typography>
                                <SubtitleTimeline
                                    ref={timelineRef}
                                    rows={timelineRows}
                                    effects={effects}
                                    selectedActionId={selectedActionId}
                                    selectedAudioSegmentId={selectedAudioSegmentId}
                                    currentTime={currentTime}
                                    defaultStyle={defaultStyle}
                                    onSubtitleClick={handleSubtitleClick}
                                    onAudioSegmentClick={setSelectedAudioSegmentId}
                                    onTimeChange={handleTimelineTimeChange}
                                    onChange={handleTimelineChange}
                                />
                            </Box>

                            {/* ── Audio Track controls (per-language) ── */}
                            <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box
                                    sx={{
                                        mb: 0.75,
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'rgba(13, 148, 136, 0.08)',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ color: 'teal' }}>
                                        Audio Track — {languageName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Mute the video&apos;s original audio and optionally replace it with a dubbed audio file. Each language keeps a separate audio track. Segments can be split and repositioned on the timeline above.
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography variant="body2">Mute Video Audio</Typography>
                                        <Switch
                                            size="small"
                                            checked={muteVideoAudio}
                                            onChange={(e) => setMuteVideoAudio(e.target.checked)}
                                        />
                                    </Stack>
                                    {/* Hidden file input */}
                                    <input
                                        ref={dubbedAudioInputRef}
                                        type="file"
                                        accept="audio/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleDubbedAudioUpload(file);
                                            e.target.value = '';
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => dubbedAudioInputRef.current?.click()}
                                    >
                                        {dubbedAudioUrl ? 'Replace Audio' : 'Upload Dubbed Audio'}
                                    </Button>
                                    {dubbedAudioUrl && (
                                        <>
                                            <Chip
                                                size="small"
                                                label={`${audioSegments[0]?.label || 'audio'} (${audioSegments.length} seg${audioSegments.length !== 1 ? 's' : ''})`}
                                                onDelete={handleClearAudio}
                                                color="default"
                                                sx={{ maxWidth: 220 }}
                                            />
                                            <Tooltip title="Split the selected audio segment at the current playhead position">
                                                <span>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                        disabled={!selectedAudioSegmentId}
                                                        onClick={handleSplitAudioSegment}
                                                    >
                                                        Split Segment
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Delete the selected audio segment">
                                                <span>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={!selectedAudioSegmentId}
                                                        onClick={() => {
                                                            setAudioSegments((prev) =>
                                                                prev.filter((s) => s.id !== selectedAudioSegmentId)
                                                            );
                                                            setSelectedAudioSegmentId('');
                                                        }}
                                                    >
                                                        Delete Segment
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Editing controls */}
                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Subtitle Formatting
                            </Typography>

                            <Box
                                sx={{
                                    mb: 1.25,
                                    p: 1,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                                    <Chip size="small" color="primary" label="Entire subtitle line" />
                                    <Chip size="small" color="warning" label="Styled words only" />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Controls are split below by scope so you can quickly see whether a setting affects the full subtitle or only highlighted words.
                                </Typography>
                            </Box>

                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2">Selected Subtitle Text</Typography>
                                    <Stack direction="row" spacing={0.75}>
                                        <Tooltip title="Split subtitle into two at the midpoint">
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CallSplitIcon />}
                                                    onClick={splitSelectedAction}
                                                    disabled={!selectedAction || (selectedAction?.text || '').trim().split(/\s+/).length < 2}
                                                >
                                                    Split
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={selectedSource?.fileUrl ? `Push all text edits back to the ${languageName} subtitle source (VTT)` : 'No subtitle source loaded'}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={<SyncIcon />}
                                                    onClick={pushToSubtitleSource}
                                                    disabled={!selectedSource?.fileUrl || (editorData?.[0]?.actions || []).length === 0}
                                                >
                                                    Push to Source
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                                <TextField
                                    multiline
                                    minRows={3}
                                    value={selectedAction?.text || ''}
                                    onChange={(event) => updateSelectedAction({ text: event.target.value })}
                                    disabled={!selectedAction}
                                />

                                <Divider sx={{ my: 0.25 }} />

                                {/* ─── Entire Subtitle Line Style ─────────────── */}
                                <Box
                                    onClick={() => setShowSubtitleStyle((v) => !v)}
                                    sx={{
                                        mt: 0.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'primary.light',
                                        bgcolor: 'rgba(37, 99, 235, 0.06)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" color="primary.main">
                                            Entire Subtitle Style
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            These settings affect the full subtitle line for the selected block.
                                        </Typography>
                                    </Box>
                                    <ExpandMoreIcon sx={{ color: 'primary.main', transform: showSubtitleStyle ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </Box>
                                <Collapse in={showSubtitleStyle}>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <TextField
                                        select
                                        size="small"
                                        label="Font"
                                        value={selectedAction?.style?.fontFamily || defaultStyle.fontFamily}
                                        onChange={(event) => updateSelectedAction({ style: { fontFamily: event.target.value } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                        SelectProps={{
                                            renderValue: (value) => (
                                                <span style={{ fontFamily: value }}>{value}</span>
                                            ),
                                        }}
                                    >
                                        {FONT_OPTIONS.map((font) => (
                                            <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                                                {font}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <NumericField
                                        size="small"
                                        label="Font Size"
                                        value={selectedAction?.style?.fontSize ?? defaultStyle.fontSize}
                                        onChange={(num) => updateSelectedAction({ style: { fontSize: num } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Line Spacing"
                                        value={selectedAction?.style?.lineHeight ?? defaultStyle.lineHeight}
                                        onChange={(num) => updateSelectedAction({ style: { lineHeight: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ step: 0.05 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Letter Spacing"
                                        value={selectedAction?.style?.letterSpacing ?? defaultStyle.letterSpacing}
                                        onChange={(num) => updateSelectedAction({ style: { letterSpacing: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: -2, max: 12, step: 0.25 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <ColorField
                                        label="Text"
                                        value={selectedAction?.style?.color || defaultStyle.color}
                                        onChange={(hex) => updateSelectedAction({ style: { color: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                    <ColorField
                                        label="Background"
                                        value={selectedAction?.style?.backgroundColor?.startsWith('#') ? selectedAction.style.backgroundColor : '#000000'}
                                        onChange={(hex) => updateSelectedAction({ style: { backgroundColor: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <NumericField
                                    size="small"
                                    label="Background Opacity (0-100)"
                                    value={selectedAction?.style?.backgroundOpacity ?? defaultStyle.backgroundOpacity}
                                    onChange={(num) =>
                                        updateSelectedAction({ style: { backgroundOpacity: clamp(num, 0, 100) } })
                                    }
                                    disabled={!selectedAction}
                                    fullWidth
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                />

                                <Stack direction="row" spacing={1}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="align-label">Align</InputLabel>
                                        <Select
                                            labelId="align-label"
                                            label="Align"
                                            value={selectedAction?.style?.align || defaultStyle.align}
                                            onChange={(event) => updateSelectedAction({ style: { align: event.target.value } })}
                                            disabled={!selectedAction}
                                        >
                                            <MenuItem value="left">Left</MenuItem>
                                            <MenuItem value="center">Center</MenuItem>
                                            <MenuItem value="right">Right</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="position-label">Position</InputLabel>
                                        <Select
                                            labelId="position-label"
                                            label="Position"
                                            value={selectedAction?.style?.position || defaultStyle.position}
                                            onChange={(event) =>
                                                updateSelectedAction({ style: { position: event.target.value, offsetX: 0, offsetY: 0 } })
                                            }
                                            disabled={!selectedAction}
                                        >
                                            <MenuItem value="top-left">Top Left</MenuItem>
                                            <MenuItem value="top-right">Top Right</MenuItem>
                                            <MenuItem value="top-center">Top Center</MenuItem>
                                            <MenuItem value="middle-left">Middle Left</MenuItem>
                                            <MenuItem value="middle-center">Middle Center</MenuItem>
                                            <MenuItem value="middle-right">Middle Right</MenuItem>
                                            <MenuItem value="bottom-left">Bottom Left</MenuItem>
                                            <MenuItem value="bottom-center">Bottom Center</MenuItem>
                                            <MenuItem value="bottom-right">Bottom Right</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Offset X (px)"
                                        value={selectedAction?.style?.offsetX ?? defaultStyle.offsetX}
                                        onChange={(num) => updateSelectedAction({ style: { offsetX: num } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Offset Y (px)"
                                        value={selectedAction?.style?.offsetY ?? defaultStyle.offsetY}
                                        onChange={(num) => updateSelectedAction({ style: { offsetY: num } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Box Width (%)"
                                        value={selectedAction?.style?.boxWidthPercent ?? defaultStyle.boxWidthPercent}
                                        onChange={(num) => updateSelectedAction({ style: { boxWidthPercent: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 10, max: 100, step: 1 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Box Radius (px)"
                                        value={selectedAction?.style?.boxRadius ?? defaultStyle.boxRadius}
                                        onChange={(num) => updateSelectedAction({ style: { boxRadius: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Box Padding X (px)"
                                        value={selectedAction?.style?.boxPaddingX ?? defaultStyle.boxPaddingX}
                                        onChange={(num) => updateSelectedAction({ style: { boxPaddingX: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 1 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Box Padding Y (px)"
                                        value={selectedAction?.style?.boxPaddingY ?? defaultStyle.boxPaddingY}
                                        onChange={(num) => updateSelectedAction({ style: { boxPaddingY: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">Bold</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.bold)}
                                        onChange={(event) => updateSelectedAction({ style: { bold: event.target.checked } })}
                                        disabled={!selectedAction}
                                    />
                                    <Typography variant="body2">Italic</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.italic)}
                                        onChange={(event) =>
                                            updateSelectedAction({ style: { italic: event.target.checked } })
                                        }
                                        disabled={!selectedAction}
                                    />
                                    <Typography variant="body2">Underline</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.underline)}
                                        onChange={(event) =>
                                            updateSelectedAction({ style: { underline: event.target.checked } })
                                        }
                                        disabled={!selectedAction}
                                    />
                                </Stack>

                                <Divider sx={{ my: 0.25 }} />

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Start (s)"
                                        value={selectedAction?.start ?? ''}
                                        onChange={(num) => updateSelectedAction({ start: num })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 0.1 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="End (s)"
                                        value={selectedAction?.end ?? ''}
                                        onChange={(num) => updateSelectedAction({ end: num })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 0.1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Fade In (ms)"
                                        value={selectedAction?.style?.fadeInMs ?? defaultStyle.fadeInMs}
                                        onChange={(num) => updateSelectedAction({ style: { fadeInMs: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Fade Out (ms)"
                                        value={selectedAction?.style?.fadeOutMs ?? defaultStyle.fadeOutMs}
                                        onChange={(num) => updateSelectedAction({ style: { fadeOutMs: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <FormControl size="small" fullWidth disabled={!selectedAction}>
                                        <InputLabel id="text-animation-label">Animation</InputLabel>
                                        <Select
                                            labelId="text-animation-label"
                                            label="Animation"
                                            value={selectedAction?.style?.textAnimationIn ?? defaultStyle.textAnimationIn}
                                            onChange={(event) =>
                                                updateSelectedAction({ style: { textAnimationIn: event.target.value } })
                                            }
                                        >
                                            {TEXT_ANIMATION_IN_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <NumericField
                                        size="small"
                                        label="Animation Duration (ms)"
                                        value={selectedAction?.style?.animationDurationMs ?? defaultStyle.animationDurationMs}
                                        onChange={(num) => updateSelectedAction({ style: { animationDurationMs: num } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                </Stack>
                                </Collapse>

                                <Divider sx={{ my: 0.25 }} />
                                {/* ─── Word Emphasis ──────────────────────────── */}
                                <Box
                                    onClick={() => setShowWordEmphasis((v) => !v)}
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'warning.light',
                                        bgcolor: 'rgba(245, 158, 11, 0.10)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" color="warning.dark">
                                            Word Emphasis Style
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            These controls affect only words listed in Styled Words.
                                        </Typography>
                                    </Box>
                                    <ExpandMoreIcon sx={{ color: 'warning.dark', transform: showWordEmphasis ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </Box>
                                <Collapse in={showWordEmphasis}>
                                {selectedAction && (() => {
                                    const rawWords = (selectedAction.text || '').trim().split(/\s+/).filter(Boolean);
                                    // Deduplicate preserving order
                                    const seen = new Set();
                                    const uniqueWords = rawWords.filter((w) => {
                                        const key = w.toLowerCase().replace(/[^a-z0-9\u0080-\uFFFF]/gi, '');
                                        if (!key || seen.has(key)) return false;
                                        seen.add(key);
                                        return true;
                                    });
                                    if (uniqueWords.length === 0) return null;
                                    const currentEmphasis = new Set(
                                        String(selectedAction?.style?.emphasisWords || '')
                                            .split(',')
                                            .map((w) => w.trim().toLowerCase())
                                            .filter(Boolean),
                                    );
                                    const toggleWord = (word) => {
                                        const key = word.toLowerCase();
                                        const next = new Set(currentEmphasis);
                                        if (next.has(key)) next.delete(key);
                                        else next.add(key);
                                        updateSelectedAction({ style: { emphasisWords: [...next].join(', ') } });
                                    };
                                    return (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                Click words to emphasize them:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {uniqueWords.map((word) => {
                                                    const isEmphasized = currentEmphasis.has(word.toLowerCase());
                                                    return (
                                                        <Chip
                                                            key={word}
                                                            label={word}
                                                            size="small"
                                                            clickable
                                                            color={isEmphasized ? 'warning' : 'default'}
                                                            variant={isEmphasized ? 'filled' : 'outlined'}
                                                            onClick={() => toggleWord(word)}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })()}

                                <TextField
                                    size="small"
                                    label="Styled Words (comma-separated)"
                                    value={selectedAction?.style?.emphasisWords ?? defaultStyle.emphasisWords}
                                    onChange={(event) =>
                                        updateSelectedAction({ style: { emphasisWords: event.target.value } })
                                    }
                                    disabled={!selectedAction}
                                    helperText="Or type words manually above"
                                />
                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Word Scale"
                                        value={selectedAction?.style?.emphasisScale ?? defaultStyle.emphasisScale}
                                        onChange={(num) => updateSelectedAction({ style: { emphasisScale: Math.max(1, num) } })}
                                        disabled={!selectedAction}
                                        inputProps={{ min: 1, step: 0.05 }}
                                        fullWidth
                                    />
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="emphasis-style-label">Word Style</InputLabel>
                                        <Select
                                            labelId="emphasis-style-label"
                                            label="Word Style"
                                            value={selectedAction?.style?.emphasisStylePreset || defaultStyle.emphasisStylePreset}
                                            onChange={(event) =>
                                                updateSelectedAction({ style: { emphasisStylePreset: event.target.value } })
                                            }
                                            disabled={!selectedAction}
                                        >
                                            <MenuItem value="scale">Scale</MenuItem>
                                            <MenuItem value="highlight">Highlight</MenuItem>
                                            <MenuItem value="outline">Outline</MenuItem>
                                            <MenuItem value="glow">Glow</MenuItem>
                                            <MenuItem value="accent">Accent</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <ColorField
                                        label="Word Color"
                                        value={selectedAction?.style?.emphasisColor || defaultStyle.emphasisColor}
                                        onChange={(hex) => updateSelectedAction({ style: { emphasisColor: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                    <ColorField
                                        label="Word BG"
                                        value={selectedAction?.style?.emphasisBgColor || defaultStyle.emphasisBgColor}
                                        onChange={(hex) => updateSelectedAction({ style: { emphasisBgColor: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Word BG Opacity"
                                        value={selectedAction?.style?.emphasisBgOpacity ?? defaultStyle.emphasisBgOpacity}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisBgOpacity: clamp(num, 0, 100) } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Word Stroke (%)"
                                        value={selectedAction?.style?.emphasisStrokeWidth ?? defaultStyle.emphasisStrokeWidth}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisStrokeWidth: clamp(num, 0, 15) } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, max: 15, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <ColorField
                                        label="Stroke Color"
                                        value={selectedAction?.style?.emphasisStrokeColor || defaultStyle.emphasisStrokeColor}
                                        onChange={(hex) => updateSelectedAction({ style: { emphasisStrokeColor: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                    <ColorField
                                        label="Shadow Color"
                                        value={selectedAction?.style?.emphasisShadowColor || defaultStyle.emphasisShadowColor}
                                        onChange={(hex) => updateSelectedAction({ style: { emphasisShadowColor: hex } })}
                                        disabled={!selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Shadow Opacity"
                                        value={selectedAction?.style?.emphasisShadowOpacity ?? defaultStyle.emphasisShadowOpacity}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisShadowOpacity: clamp(num, 0, 100) } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Shadow Blur"
                                        value={selectedAction?.style?.emphasisShadowBlur ?? defaultStyle.emphasisShadowBlur}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisShadowBlur: Math.max(0, num) } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: 0, max: 40, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Word Weight"
                                        value={selectedAction?.style?.emphasisFontWeight ?? defaultStyle.emphasisFontWeight}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisFontWeight: clamp(num, 100, 900) } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: 100, max: 900, step: 100 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Word Letter Spacing"
                                        value={selectedAction?.style?.emphasisLetterSpacing ?? defaultStyle.emphasisLetterSpacing}
                                        onChange={(num) =>
                                            updateSelectedAction({ style: { emphasisLetterSpacing: num } })
                                        }
                                        disabled={!selectedAction}
                                        inputProps={{ min: -2, max: 8, step: 0.5 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">Word Bold</Typography>
                                    <Switch
                                        checked={selectedAction?.style?.emphasisBold !== undefined ? Boolean(selectedAction?.style?.emphasisBold) : true}
                                        onChange={(event) => updateSelectedAction({ style: { emphasisBold: event.target.checked } })}
                                        disabled={!selectedAction}
                                    />
                                    <Typography variant="body2">Word Italic</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.emphasisItalic)}
                                        onChange={(event) => updateSelectedAction({ style: { emphasisItalic: event.target.checked } })}
                                        disabled={!selectedAction}
                                    />
                                    <Typography variant="body2">Word Underline</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.emphasisUnderline)}
                                        onChange={(event) => updateSelectedAction({ style: { emphasisUnderline: event.target.checked } })}
                                        disabled={!selectedAction}
                                    />
                                    <Typography variant="body2">Word Uppercase</Typography>
                                    <Switch
                                        checked={Boolean(selectedAction?.style?.emphasisUppercase)}
                                        onChange={(event) => updateSelectedAction({ style: { emphasisUppercase: event.target.checked } })}
                                        disabled={!selectedAction}
                                    />
                                </Stack>
                                </Collapse>

                                <Divider sx={{ my: 0.25 }} />
                                <Box
                                    onClick={() => setShowShapeOverlay((v) => !v)}
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'info.light',
                                        bgcolor: 'rgba(2, 132, 199, 0.10)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" color="info.dark">
                                            Shape / Box Overlay
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Add or position shape overlays for all subtitles (global) or only this selected subtitle.
                                        </Typography>
                                    </Box>
                                    <ExpandMoreIcon sx={{ color: 'info.dark', transform: showShapeOverlay ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </Box>
                                <Collapse in={showShapeOverlay}>

                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">
                                        {shapeOverlayScope === 'global' ? 'Enable Shape' : 'Enable Subtitle Shape'}
                                    </Typography>
                                    <Switch
                                        checked={Boolean(
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.enabled
                                                : selectedAction?.style?.localShapeEnabled,
                                        )}
                                        onChange={(event) => {
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, enabled: event.target.checked }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeEnabled: event.target.checked } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                    />
                                </Stack>

                                <FormControl>
                                    <FormLabel sx={{ fontSize: '0.75rem' }}>Scope</FormLabel>
                                    <RadioGroup
                                        row
                                        value={shapeOverlayScope}
                                        onChange={(event) => setShapeOverlayScope(event.target.value)}
                                    >
                                        <FormControlLabel value="local" control={<Radio size="small" />} label="Selected Subtitle" disabled={!selectedAction} />
                                        <FormControlLabel value="global" control={<Radio size="small" />} label="Global" />
                                    </RadioGroup>
                                </FormControl>

                                <Stack direction="row" spacing={1}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="shape-type-combined-label">Shape Type</InputLabel>
                                        <Select
                                            labelId="shape-type-combined-label"
                                            label="Shape Type"
                                            value={
                                                shapeOverlayScope === 'global'
                                                    ? shapeOverlay.type
                                                    : selectedAction?.style?.localShapeType || defaultStyle.localShapeType
                                            }
                                            onChange={(event) => {
                                                if (shapeOverlayScope === 'global') {
                                                    setShapeOverlay((previous) => ({ ...previous, type: event.target.value }));
                                                    return;
                                                }
                                                updateSelectedAction({ style: { localShapeType: event.target.value } });
                                            }}
                                            disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        >
                                            <MenuItem value="box">Box</MenuItem>
                                            <MenuItem value="rounded-box">Rounded Box</MenuItem>
                                            <MenuItem value="circle">Circle</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Rotate"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.rotationDeg
                                                : selectedAction?.style?.localShapeRotationDeg ?? defaultStyle.localShapeRotationDeg
                                        }
                                        onChange={(event) => {
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({
                                                    ...previous,
                                                    rotationDeg: Number(event.target.value) || 0,
                                                }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeRotationDeg: Number(event.target.value) || 0 } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: -360, max: 360, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="X (%)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.xPercent
                                                : selectedAction?.style?.localShapeXPercent ?? defaultStyle.localShapeXPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, xPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeXPercent: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Y (%)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.yPercent
                                                : selectedAction?.style?.localShapeYPercent ?? defaultStyle.localShapeYPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, yPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeYPercent: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Width (%)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.widthPercent
                                                : selectedAction?.style?.localShapeWidthPercent ?? defaultStyle.localShapeWidthPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 1, 1, 100);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, widthPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeWidthPercent: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Height (%)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.heightPercent
                                                : selectedAction?.style?.localShapeHeightPercent ?? defaultStyle.localShapeHeightPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 1, 1, 100);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, heightPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeHeightPercent: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <ColorField
                                        label="Fill"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.fillColor
                                                : selectedAction?.style?.localShapeFillColor || defaultStyle.localShapeFillColor
                                        }
                                        onChange={(hex) => {
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, fillColor: hex }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeFillColor: hex } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Fill Opacity"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.fillOpacity
                                                : selectedAction?.style?.localShapeFillOpacity ?? defaultStyle.localShapeFillOpacity
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, fillOpacity: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeFillOpacity: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <ColorField
                                        label="Border"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.borderColor
                                                : selectedAction?.style?.localShapeBorderColor || defaultStyle.localShapeBorderColor
                                        }
                                        onChange={(hex) => {
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, borderColor: hex }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeBorderColor: hex } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Border Width"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.borderWidth
                                                : selectedAction?.style?.localShapeBorderWidth ?? defaultStyle.localShapeBorderWidth
                                        }
                                        onChange={(event) => {
                                            const nextValue = Math.max(0, Number(event.target.value) || 0);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, borderWidth: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeBorderWidth: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, max: 20, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Radius (px)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.radiusPx ?? defaultShapeOverlay.radiusPx
                                                : selectedAction?.style?.localShapeRadiusPx ?? defaultStyle.localShapeRadiusPx
                                        }
                                        onChange={(num) => {
                                            const nextValue = Math.max(0, num);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, radiusPx: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeRadiusPx: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, step: 1 }}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Fade In (ms)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.fadeInMs ?? defaultShapeOverlay.fadeInMs
                                                : selectedAction?.style?.localShapeFadeInMs ?? Math.round((selectedAction?.start ?? 0) * 1000)
                                        }
                                        onChange={(num) => {
                                            const nextValue = Math.max(0, num);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, fadeInMs: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeFadeInMs: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Fade Out (ms)"
                                        value={
                                            shapeOverlayScope === 'global'
                                                ? shapeOverlay.fadeOutMs ?? defaultShapeOverlay.fadeOutMs
                                                : selectedAction?.style?.localShapeFadeOutMs ?? Math.round((selectedAction?.end ?? 0) * 1000)
                                        }
                                        onChange={(num) => {
                                            const nextValue = Math.max(0, num);
                                            if (shapeOverlayScope === 'global') {
                                                setShapeOverlay((previous) => ({ ...previous, fadeOutMs: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localShapeFadeOutMs: nextValue } });
                                        }}
                                        disabled={shapeOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                </Stack>
                                </Collapse>

                                <Divider sx={{ my: 0.25 }} />
                                <Box
                                    onClick={() => setShowImageOverlay((v) => !v)}
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'success.light',
                                        bgcolor: 'rgba(22, 163, 74, 0.10)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" color="success.dark">
                                            Image Overlay
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Add a logo/image globally or attach an image just to the selected subtitle block.
                                        </Typography>
                                    </Box>
                                    <ExpandMoreIcon sx={{ color: 'success.dark', transform: showImageOverlay ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </Box>
                                <Collapse in={showImageOverlay}>

                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2">
                                        {imageOverlayScope === 'global' ? 'Enable Logo/Image' : 'Enable Subtitle Image'}
                                    </Typography>
                                    <Switch
                                        checked={Boolean(
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.enabled
                                                : selectedAction?.style?.localLogoEnabled,
                                        )}
                                        onChange={(event) => {
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, enabled: event.target.checked }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoEnabled: event.target.checked } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                    />
                                </Stack>

                                <FormControl>
                                    <FormLabel sx={{ fontSize: '0.75rem' }}>Scope</FormLabel>
                                    <RadioGroup
                                        row
                                        value={imageOverlayScope}
                                        onChange={(event) => setImageOverlayScope(event.target.value)}
                                    >
                                        <FormControlLabel value="local" control={<Radio size="small" />} label="Selected Subtitle" disabled={!selectedAction} />
                                        <FormControlLabel value="global" control={<Radio size="small" />} label="Global" />
                                    </RadioGroup>
                                </FormControl>

                                <TextField
                                    size="small"
                                    label={imageOverlayScope === 'global' ? 'Image URL' : 'Subtitle Image URL'}
                                    value={
                                        imageOverlayScope === 'global'
                                            ? logoOverlay.imageUrl
                                            : selectedAction?.style?.localLogoImageUrl || defaultStyle.localLogoImageUrl
                                    }
                                    onChange={(event) => {
                                        if (imageOverlayScope === 'global') {
                                            setLogoOverlay((previous) => ({ ...previous, imageUrl: event.target.value }));
                                            return;
                                        }
                                        updateSelectedAction({ style: { localLogoImageUrl: event.target.value } });
                                    }}
                                    disabled={imageOverlayScope === 'local' && !selectedAction}
                                    fullWidth
                                />

                                <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    disabled={imageOverlayScope === 'local' && !selectedAction}
                                >
                                    {imageOverlayScope === 'global' ? 'Upload Logo / Image' : 'Upload Subtitle Image'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={imageOverlayScope === 'global' ? handleLogoUpload : handleLocalLogoUpload}
                                    />
                                </Button>

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Image X (%)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.xPercent
                                                : selectedAction?.style?.localLogoXPercent ?? defaultStyle.localLogoXPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, xPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoXPercent: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Image Y (%)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.yPercent
                                                : selectedAction?.style?.localLogoYPercent ?? defaultStyle.localLogoYPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, yPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoYPercent: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Image Width (%)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.widthPercent
                                                : selectedAction?.style?.localLogoWidthPercent ?? defaultStyle.localLogoWidthPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 1, 1, 100);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, widthPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoWidthPercent: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Image Height (%)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.heightPercent
                                                : selectedAction?.style?.localLogoHeightPercent ?? defaultStyle.localLogoHeightPercent
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 1, 1, 100);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, heightPercent: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoHeightPercent: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Image Opacity"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.opacity
                                                : selectedAction?.style?.localLogoOpacity ?? defaultStyle.localLogoOpacity
                                        }
                                        onChange={(event) => {
                                            const nextValue = clamp(Number(event.target.value) || 0, 0, 100);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, opacity: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoOpacity: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        fullWidth
                                    />
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="image-fit-combined-label">Fit</InputLabel>
                                        <Select
                                            labelId="image-fit-combined-label"
                                            label="Fit"
                                            value={
                                                imageOverlayScope === 'global'
                                                    ? logoOverlay.fit
                                                    : selectedAction?.style?.localLogoFit || defaultStyle.localLogoFit
                                            }
                                            onChange={(event) => {
                                                if (imageOverlayScope === 'global') {
                                                    setLogoOverlay((previous) => ({ ...previous, fit: event.target.value }));
                                                    return;
                                                }
                                                updateSelectedAction({ style: { localLogoFit: event.target.value } });
                                            }}
                                            disabled={imageOverlayScope === 'local' && !selectedAction}
                                        >
                                            <MenuItem value="contain">Contain</MenuItem>
                                            <MenuItem value="cover">Cover</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                {imageOverlayScope === 'global' && (
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Show from (s)"
                                            value={logoOverlay.startSec ?? ''}
                                            onChange={(e) =>
                                                setLogoOverlay((prev) => ({
                                                    ...prev,
                                                    startSec: e.target.value === '' ? null : Number(e.target.value),
                                                }))
                                            }
                                            onFocus={(e) => e.target.select()}
                                            placeholder="0"
                                            helperText="Leave blank = always"
                                            inputProps={{ min: 0, step: 0.1 }}
                                            fullWidth
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Hide after (s)"
                                            value={logoOverlay.endSec ?? ''}
                                            onChange={(e) =>
                                                setLogoOverlay((prev) => ({
                                                    ...prev,
                                                    endSec: e.target.value === '' ? null : Number(e.target.value),
                                                }))
                                            }
                                            onFocus={(e) => e.target.select()}
                                            placeholder="end"
                                            helperText="Leave blank = always"
                                            inputProps={{ min: 0, step: 0.1 }}
                                            fullWidth
                                        />
                                    </Stack>
                                )}

                                <Stack direction="row" spacing={1}>
                                    <NumericField
                                        size="small"
                                        label="Fade In (ms)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.fadeInMs ?? defaultLogoOverlay.fadeInMs
                                                : selectedAction?.style?.localLogoFadeInMs ?? Math.round((selectedAction?.start ?? 0) * 1000)
                                        }
                                        onChange={(num) => {
                                            const nextValue = Math.max(0, num);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, fadeInMs: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoFadeInMs: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                    <NumericField
                                        size="small"
                                        label="Fade Out (ms)"
                                        value={
                                            imageOverlayScope === 'global'
                                                ? logoOverlay.fadeOutMs ?? defaultLogoOverlay.fadeOutMs
                                                : selectedAction?.style?.localLogoFadeOutMs ?? Math.round((selectedAction?.end ?? 0) * 1000)
                                        }
                                        onChange={(num) => {
                                            const nextValue = Math.max(0, num);
                                            if (imageOverlayScope === 'global') {
                                                setLogoOverlay((previous) => ({ ...previous, fadeOutMs: nextValue }));
                                                return;
                                            }
                                            updateSelectedAction({ style: { localLogoFadeOutMs: nextValue } });
                                        }}
                                        disabled={imageOverlayScope === 'local' && !selectedAction}
                                        inputProps={{ min: 0, step: 50 }}
                                        fullWidth
                                    />
                                </Stack>
                                </Collapse>

                                <Divider sx={{ my: 0.25 }} />
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'secondary.light',
                                        bgcolor: 'rgba(147, 51, 234, 0.08)',
                                    }}
                                >
                                    <Typography variant="subtitle2" color="secondary.dark">
                                        Video Fade
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Controls how quickly the main video fades in at the beginning and fades out at the end (in milliseconds).
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Video Fade In (ms)"
                                        value={videoFadeInMs}
                                        onChange={(event) => setVideoFadeInMs(Number(event.target.value) || 0)}
                                        fullWidth
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Video Fade Out (ms)"
                                        value={videoFadeOutMs}
                                        onChange={(event) => setVideoFadeOutMs(Number(event.target.value) || 0)}
                                        fullWidth
                                    />
                                </Stack>

                            </Stack>
                        </Paper>

                    </Grid>
                    {/* ── end RIGHT column ── */}
                </Grid>
            </Box>
            <HelpOverlay helpItems={reelEditorHelpItems} title="Reel Editor Guide" buttonId="help-reel-editor" />
            <BulkReelExportDialog
                open={bulkExportOpen}
                onClose={() => setBulkExportOpen(false)}
                projects={projects}
                buildExportPayload={buildExportPayloadForProject}
            />
        </Box>
    );
};

export default ReelEditorPage;
